import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import heic2any from "heic2any";
import { getDocs, collection, addDoc, deleteDoc, doc, updateDoc, setDoc, getDoc, serverTimestamp, where } from "firebase/firestore";
import { db } from "./firebase";
import { auth } from "./firebase";
import * as Sentry from "@sentry/react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  signInWithCustomToken,
  sendEmailVerification,
  sendPasswordResetEmail,
  getAdditionalUserInfo,
  fetchSignInMethodsForEmail,
  linkWithCredential,
} from "firebase/auth";

import { query, orderBy, limit, startAfter } from "firebase/firestore"
import AdminPage from "./AdminPage";
import { validateRentalForm } from "./rentalValidation";

const IS_DEV = process.env.NODE_ENV !== "production";
const logDebug = (...args) => {
  if (IS_DEV) console.log(...args);
};
const logWarn = (...args) => {
  if (IS_DEV) console.warn(...args);
};

const reportPermissionDenied = (context, error) => {
  const message = error?.message || "permission-denied";
  logWarn(`[permission-denied] ${context}:`, message);
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.captureMessage("permission-denied", {
      level: "warning",
      extra: { context, message },
    });
  }
};

const providerLabel = (method) => {
  switch (method) {
    case "password":
      return "이메일/비밀번호";
    case "google.com":
      return "Google";
    case "kakao":
      return "Kakao";
    default:
      return method || "기존 로그인";
  }
};

const KAKAO_PENDING_LINK_KEY = "kakao_pending_link";

const savePendingKakaoLink = (payload) => {
  try {
    localStorage.setItem(KAKAO_PENDING_LINK_KEY, JSON.stringify(payload));
  } catch (_) {}
};

const loadPendingKakaoLink = () => {
  try {
    const raw = localStorage.getItem(KAKAO_PENDING_LINK_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const clearPendingKakaoLink = () => {
  try {
    localStorage.removeItem(KAKAO_PENDING_LINK_KEY);
  } catch (_) {}
};

// Admin allowlist (comma-separated emails in REACT_APP_ADMIN_EMAILS)
const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "")
  .split(",")
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

logDebug("[INIT] REACT_APP_ADMIN_EMAILS:", process.env.REACT_APP_ADMIN_EMAILS);
logDebug("[INIT] ADMIN_EMAILS array:", ADMIN_EMAILS);

const isAdminEmail = (email) => {
  if (!email) return false;
  const result = ADMIN_EMAILS.includes(email.toLowerCase());
  logDebug("[isAdminEmail] checking:", email, "->", result, "against:", ADMIN_EMAILS);
  return result;
};

const isAdminUser = (firebaseUser) => {
  if (!firebaseUser) return false;
  const emailAdmin = isAdminEmail(firebaseUser.email);
  logDebug("[DEBUG] isAdminUser check:", {
    userEmail: firebaseUser.email,
    isAdminEmail: emailAdmin,
    providerData: firebaseUser.providerData,
    providerDataLength: firebaseUser.providerData?.length,
    allProviderIds: firebaseUser.providerData?.map(p => p?.providerId),
  });
  
  // 이메일/비밀번호 로그인: providerData에 "password" provider가 있는지 확인
  const hasPasswordProvider = firebaseUser.providerData?.some(
    (p) => p?.providerId === "password"
  );
    
  logDebug("[DEBUG] hasPasswordProvider:", hasPasswordProvider, "adminResult:", emailAdmin && hasPasswordProvider);
  return emailAdmin && hasPasswordProvider;
};

export default function RentList() {
  const NOTICES_LIMIT = 50;
  const MESSAGES_PAGE_SIZE = 20;
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);
  const [authError, setAuthError] = useState("");
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [lastEmail, setLastEmail] = useState("");
  const kakaoResumeAttempted = useRef(false);
  const isSigningUp = useRef(false);
  const blockAuthSync = useRef(false);
  const [pendingEmailVerification, setPendingEmailVerification] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showLoginHelpModal, setShowLoginHelpModal] = useState(false);
  const [loginHelpMessage, setLoginHelpMessage] = useState("");
  const [pendingLinkCredential, setPendingLinkCredential] = useState(null);
  const [pendingLinkEmail, setPendingLinkEmail] = useState("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("안내");
  const [infoModalMessage, setInfoModalMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState("확인");
  const [confirmModalMessage, setConfirmModalMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPage, setShowAdminPage] = useState(false);
  // duplicateNotice state removed (unused)
  const [notices, setNotices] = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(false);
  const [showNoticePopup, setShowNoticePopup] = useState(false);
  const [noticePopup, setNoticePopup] = useState(null);
  const [showEmailCollectModal, setShowEmailCollectModal] = useState(false);
  const [emailCollectValue, setEmailCollectValue] = useState("");
  const [emailCollectError, setEmailCollectError] = useState("");
  const [emailCollectUser, setEmailCollectUser] = useState(null);
  const [emailCollectProvider, setEmailCollectProvider] = useState("");
  const emailCollectResolver = useRef(null);
  const confirmResolver = useRef(null);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [area, setArea] = useState("");
  const [city, setCity] = useState("");
  const [price, setPrice] = useState("");
  const [search, setSearch] = useState("");
  // ✅ 2차 필터 입력값(검색용)
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [parkingSpots, setParkingSpots] = useState("");
  const [parkingRequired, setParkingRequired] = useState(false);
  const [furnishedOnly, setFurnishedOnly] = useState(false);
  const [petFriendly, setPetFriendly] = useState(false);
  const [utilitiesIncluded, setUtilitiesIncluded] = useState(false);
  const [internetIncluded, setInternetIncluded] = useState(false);
  const [genderFilter, setGenderFilter] = useState("");
  const [showDetailFilters, setShowDetailFilters] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    // 초기에는 항상 닫힘 상태로 시작
    setShowDetailFilters(false);
  }, []);
  const [filters, setFilters] = useState({
    category: "",
    area: "",
    city: "",
    price: "",
    keyword: "",
    bedrooms: "",
    bathrooms: "",
    parkingSpots: "",
    parkingRequired: false,
    furnishedOnly: false,
    gender: "",
    utilitiesIncluded: false,
    internetIncluded: false,
    petFriendly: false,
  });

  const isWholeHomeCategory = (cat) =>
      (cat || "").trim() === "콘도(전체렌트)" || (cat || "").trim() === "하우스(전체렌트)";

    const shouldShowGender = (cat) => !isWholeHomeCategory(cat);

  const isRentRequestCategory = (cat) => (cat || "").trim() === "렌트구합니다";

  // const isWholeHomeSearch = isWholeHomeCategory(selectedCategory); // unused

  // 비밀번호 규칙 검사용 유틸
  const tooCommonPasswords = [
    "password", "123456", "12345678", "123456789", "qwerty", "iloveyou", "111111", "abc123"
  ];
  const isSequential = (s) => /0123|1234|2345|3456|4567|5678|6789|abcd|bcde|cdef|qwer|asdf/i.test(s);
  const hasRepeat = (s) => /(.)\1\1/.test(s);
  const getPasswordErrors = (pw, emailVal = "") => {
    const errors = [];
    if (!pw || pw.length < 8) errors.push("8자 이상이어야 합니다.");
    if (pw.length > 64) errors.push("64자 이하여야 합니다.");
    const classes = [/[A-Z]/, /[a-z]/, /[0-9]/, /[^A-Za-z0-9]/].filter((re) => re.test(pw)).length;
    if (classes < 3) errors.push("대문자·소문자·숫자·특수문자 중 3가지 이상을 포함하세요.");
    if (/\s/.test(pw)) errors.push("공백은 사용할 수 없습니다.");
    const local = emailVal.split("@")[0]?.toLowerCase();
    if (local && pw.toLowerCase().includes(local)) errors.push("이메일과 비밀번호가 너무 유사합니다.");
    if (isSequential(pw) || hasRepeat(pw) || tooCommonPasswords.includes(pw.toLowerCase())) {
      errors.push("너무 쉬운 패턴/단어는 사용할 수 없습니다.");
    }

    return errors;
  };

  // 지역별 도시 매핑
  const citiesByArea = {
    "광역토론토(GTA)": [
      "토론토(Toronto)",
      "노스욕(North York)",
      "마컴(Markham)",
      "리치몬드힐(Richmond Hill)",
      "번(Vaughan)",
      "뉴마켓(Newmarket)",
      "스카보르(Scarborough)",
      "미시사가(Mississauga)",
      "브램턴(Brampton)",
      "이토비코(Etobicoke)",
      "피커링(Pickering)",
      "오샤와(Oshawa)",
      "옥빌(Oakville)",
      "밀튼(Milton)",
      "벌링턴(Burlington)"
    ],
    "온타리오(기타도시)": [
      "워털루(Waterloo)",
      "키치너(Kitchener)",
      "해밀턴(Hamilton)",
      "런던(London)",
      "구엘프(Guelph)",
      "오타와(Ottawa)",
      "베리(Barrie)",
      "나이아가라(Niagara Region)",
      "킹스턴(Kingston)",
      "캠브리지(Cambridge)",
      "윈저(Windsor)",
      "기타도시(Other Cities)"
    ],
    "광역벤쿠버(GVA)": [
      "벤쿠버(Vancouver)",
      "버나비(Burnaby)",
      "리치먼드(Richmond)",
      "코퀴틀람(Coquitlam)",
      "서리(Surrey)",
      "노스벤쿠버(North Vancouver)",
      "뉴웨스트민스터(New Westminster)"
    ],
    "브리티시컬럼비아(기타도시)": [
      "랭리(Langley)",
      "델타(Delta)",
      "메이플릿지(Maple Ridge)",
      "빅토리아(Victoria)",
      "켈로나(Kelowna)",
      "아보츠포드(Abbotsford)",
      "기타도시(Other Cities)"
    ],
    "캘거리": [
      "캘거리(Calgary)",
      "에어드리(Airdrie)",
      "코크린(Cochrane)"
    ],
    "에드몬튼": [
      "에드몬튼(Edmonton)",
      "셔우드파크(Sherwood Park)",
      "세인트앨버트(St. Albert)"
    ],
    "마니토바": ["위니펙(Winnipeg)"],
    "퀘벡": [
      "몬트리올(Montreal)",
      "라발(Laval)",
      "브로사르(Brossard)"
    ],
    "노바스코샤": [
      "할리팩스(Halifax)",
      "다트머스(Dartmouth)"
    ]
  };

  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showMyListings, setShowMyListings] = useState(() => {
    const saved = localStorage.getItem('showMyListings');
    return saved === 'true';
  });
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showInbox, setShowInbox] = useState(false);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [replyContent, setReplyContent] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [hasMoreInbox, setHasMoreInbox] = useState(true);
  const [lastReceivedDoc, setLastReceivedDoc] = useState(null);
  const [lastSentDoc, setLastSentDoc] = useState(null);
  const isMessagesLoadingRef = useRef(false);
  const lastReceivedDocRef = useRef(null);
  const lastSentDocRef = useRef(null);
  const initialRentsLoadedRef = useRef(false);
  // 환경 변수 또는 임시 하드코딩 값 사용
  //const KAKAO_JS_KEY = process.env.REACT_APP_KAKAO_JS_KEY || "3bf70a4341761f378e9cdf0d037e7d40";
  //const KAKAO_CUSTOM_TOKEN_ENDPOINT = process.env.REACT_APP_KAKAO_CUSTOM_TOKEN_ENDPOINT || "https://us-central1-koreanhome-f1d2e.cloudfunctions.net/kakaoCustomToken";
  //const VERIFY_EXISTING_USER_ENDPOINT = process.env.REACT_APP_VERIFY_EXISTING_USER_ENDPOINT || "https://us-central1-koreanhome-f1d2e.cloudfunctions.net/verifyExistingUser";
  //const KAKAO_REDIRECT_URI = typeof window !== "undefined" ? window.location.origin : "https://koreanhome-f1d2e.web.app";
  
  // ===== ENV CONFIG =====
const KAKAO_JS_KEY = process.env.REACT_APP_KAKAO_JS_KEY;
const KAKAO_CUSTOM_TOKEN_ENDPOINT =
  process.env.REACT_APP_KAKAO_CUSTOM_TOKEN_ENDPOINT;

const KAKAO_LINK_ENDPOINT =
  process.env.REACT_APP_KAKAO_LINK_ENDPOINT;

const VERIFY_EXISTING_USER_ENDPOINT =
  process.env.REACT_APP_VERIFY_EXISTING_USER_ENDPOINT;

const KAKAO_REDIRECT_URI =
  process.env.REACT_APP_KAKAO_REDIRECT_URI;

const GOOGLE_MAPS_EMBED_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY;

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY;



// ===== ENV SAFETY CHECK =====
if (!KAKAO_JS_KEY) {
  throw new Error("Missing REACT_APP_KAKAO_JS_KEY");
}

if (!KAKAO_CUSTOM_TOKEN_ENDPOINT) {
  throw new Error("Missing REACT_APP_KAKAO_CUSTOM_TOKEN_ENDPOINT");
}

if (!KAKAO_LINK_ENDPOINT) {
  logWarn("Missing REACT_APP_KAKAO_LINK_ENDPOINT");
}

if (!VERIFY_EXISTING_USER_ENDPOINT) {
  throw new Error("Missing REACT_APP_VERIFY_EXISTING_USER_ENDPOINT");
}

if (!KAKAO_REDIRECT_URI) {
  throw new Error("Missing REACT_APP_KAKAO_REDIRECT_URI");
}

if (!GOOGLE_MAPS_EMBED_KEY) {
  logWarn("Missing REACT_APP_GOOGLE_MAPS_EMBED_KEY");
}

if (!GOOGLE_MAPS_API_KEY) {
  logWarn("Missing REACT_APP_GOOGLE_MAPS_API_KEY");
}

  const loadGoogleMapsScript = useCallback(() => {
    if (typeof window === "undefined") return;
    if (window.google?.maps) return;
    if (!GOOGLE_MAPS_API_KEY) return;
    if (document.querySelector('script[data-google-maps="true"]')) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps", "true");
    document.head.appendChild(script);
  }, [GOOGLE_MAPS_API_KEY]);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);


const getKakaoRedirectUri = useCallback(() => {
  if (typeof window === "undefined") return KAKAO_REDIRECT_URI;
  // Prefer current origin on mobile so the flow returns to the active site.
  return window.location.origin || KAKAO_REDIRECT_URI;
}, [KAKAO_REDIRECT_URI]);

  const [form, setForm] = useState({
    images: [],
    title: "",
    postalCode: "",
    postalCodeCoords: null,
    price: "",
    gender: "",
    date: "",
    description: "",
    category: "",
    area: "",
    city: "",

    // ✅ 2차 필터 저장 필드 (콘도/하우스용)
    bedrooms: "",      // "0","1","2","3","4"(4=4+)
    bathrooms: "",     // "1","1.5","2","2.5","3"(3=3+)
    parkingSpots: "",  // "0","1","2"(2=2+)
    parkingAvailable: false,
    furnished: false,  // boolean
    utilitiesIncluded: false,
    internetIncluded: false,
    petFriendly: false,
  });
  const [dragIndex, setDragIndex] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const geocoderRef = useRef(null);
  const postalCodeDebounceRef = useRef(null);
  const lastGeocodeRef = useRef("");

  const generateFuzzedCoords = (lat, lng) => {
    const fuzzDistance = 0.003; // ~300 meters
    const fuzzLat = lat + (Math.random() - 0.5) * fuzzDistance;
    const fuzzLng = lng + (Math.random() - 0.5) * fuzzDistance;
    return { lat: fuzzLat, lng: fuzzLng };
  };

  useEffect(() => {
    return () => {
      if (postalCodeDebounceRef.current) {
        clearTimeout(postalCodeDebounceRef.current);
      }
    };
  }, []);

  const handlePostalCodeGeocoding = async (postalCode) => {
    if (!postalCode || postalCode.length < 3) return;
    
    // Google Maps API 로드 대기
    let attempts = 0;
    while (!window.google?.maps && attempts < 20) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    if (!window.google?.maps) {
      console.error("Google Maps API not loaded");
      return;
    }

    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
      logDebug("Geocoder initialized");
    }

    try {
      const query = postalCode + ", Canada";
      logDebug("Geocoding query:", query);
      
      const results = await geocoderRef.current.geocode({
        address: query
      });
      
      logDebug("Geocoding results:", results);
      
      if (results && results[0]) {
        const { location } = results[0].geometry;
        const fuzzed = generateFuzzedCoords(location.lat(), location.lng());
        logDebug("Fuzzed coords:", fuzzed);
        setForm(prev => {
          const updated = { ...prev, postalCodeCoords: fuzzed };
          logDebug("Form updated with coords:", updated.postalCodeCoords);
          return updated;
        });
      } else {
        logWarn("No geocoding results found for:", query);
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
  }; // { images: [], index: number }

  const formatBedroomsDisplay = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return String(value);
    const whole = Math.floor(numeric);
    const hasDen = Math.abs(numeric - whole - 0.5) < 0.0001;
    if (hasDen) {
      return whole > 0 ? `${whole}+Den` : "Den";
    }
    return String(numeric);
  };

  // const isWholeHomeForm = isWholeHomeCategory(form.category); // unused
  const [rents, setRents] = useState([]);
  const [selectedRent, setSelectedRent] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const rentsPageSize = 30;
  const [isRentsLoading, setIsRentsLoading] = useState(false);
  const [hasMoreRents, setHasMoreRents] = useState(true);
  const [lastRentDoc, setLastRentDoc] = useState(null);

  const categories = [
    "콘도(전체렌트)", "하우스(전체렌트)", "룸렌트/부분렌트", "서블렛", "룸메이트", "민박", "홈스테이(하숙)", "렌트구합니다"
  ];

  const categoryButtons = [
    "콘도(전체렌트)",
    "하우스(전체렌트)",
    "룸렌트/부분렌트",
    "서블렛",
    "룸메이트",
    "민박",
    "홈스테이(하숙)",
    "렌트구합니다",
  ];

  const categoryLabels = {
    "콘도(전체렌트)": "🏢 콘도",
    "하우스(전체렌트)": "🏠 하우스",
    "룸렌트/부분렌트": "🛏 부분·룸렌트",
    "서블렛": "🔁 서브렛",
    "룸메이트": "👥 룸메이트",
    "민박": "🧳 민박",
    "홈스테이(하숙)": "🏡 홈스테이",
    "렌트구합니다": "📢 렌트구함",
  };

  const schedulePostalCodeGeocoding = (postalCode) => {
    if (postalCodeDebounceRef.current) {
      clearTimeout(postalCodeDebounceRef.current);
    }

    if (!postalCode || postalCode.length < 3) {
      lastGeocodeRef.current = "";
      setForm(prev => ({ ...prev, postalCodeCoords: null }));
      return;
    }

    postalCodeDebounceRef.current = setTimeout(() => {
      if (postalCode === lastGeocodeRef.current) return;
      lastGeocodeRef.current = postalCode;
      handlePostalCodeGeocoding(postalCode);
    }, 400);
  };

  const summarizeConditions = (rent) => {
    if (!rent) return "없음";
    const parts = [];
    if (rent.furnished) parts.push("가구 포함");
    if (rent.utilitiesIncluded) parts.push("유틸리티 포함(전부/일부)");
    if (rent.internetIncluded) parts.push("인터넷 포함");
    if (rent.petFriendly) parts.push("반려동물 가능");
    const parkingSpots = Number(rent.parkingSpots || 0);
    if (rent.parkingAvailable || parkingSpots > 0) {
      parts.push(parkingSpots > 0 ? `주차 ${parkingSpots}대` : "주차 가능");
    }
    if (rent.bedrooms !== null && rent.bedrooms !== undefined && rent.bedrooms !== "") {
      parts.push(`침실 ${formatBedroomsDisplay(rent.bedrooms)}`);
    }
    if (rent.bathrooms) parts.push(`욕실 ${rent.bathrooms}개`);
    return parts.length ? parts.join(", ") : "없음";
  };

  // createdAt가 문자열/Date/Firestore Timestamp 모두 정렬 가능하도록 변환
  const toTimestamp = useCallback((createdAt) => {
    if (!createdAt) return 0;
    if (createdAt.seconds) return createdAt.seconds * 1000;
    if (createdAt.toMillis) return createdAt.toMillis();
    if (createdAt instanceof Date) return createdAt.getTime();
    const parsed = Date.parse(createdAt);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  // 노출갱신(lastRefreshedAt)을 우선하여 정렬 기준 계산 (미존재 시 createdAt 사용)
  const getSortTimestamp = useCallback((rent) => {
    const sortTs = toTimestamp(rent.sortTimestamp);
    if (sortTs) return sortTs;
    const refreshed = toTimestamp(rent.lastRefreshedAt);
    const updated = toTimestamp(rent.updatedAt);
    const created = toTimestamp(rent.createdAt);
    return Math.max(refreshed, updated, created);
  }, [toTimestamp]);

  const mergeMessagesById = (base, incoming) => {
    const map = new Map();
    [...base, ...incoming].forEach((msg) => {
      map.set(msg.id, msg);
    });
    return Array.from(map.values());
  };

  const sortMessagesDesc = useCallback((a, b) => (
    toTimestamp(b.createdAt) - toTimestamp(a.createdAt)
  ), [toTimestamp]);

  const fetchRents = useCallback(async ({ reset = false } = {}) => {
    if (isRentsLoading) return;
    setIsRentsLoading(true);

    if (reset) {
      setLastRentDoc(null);
      setHasMoreRents(true);
    }

    try {
      const baseQuery = query(
        collection(db, "rentals"),
        orderBy("sortTimestamp", "desc"),
        limit(rentsPageSize)
      );
      const q = (!reset && lastRentDoc)
        ? query(
            collection(db, "rentals"),
            orderBy("sortTimestamp", "desc"),
            startAfter(lastRentDoc),
            limit(rentsPageSize)
          )
        : baseQuery;

      const snapshot = await getDocs(q);
      const rentData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      setRents(prev => (reset ? rentData : [...prev, ...rentData]));
      setLastRentDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMoreRents(snapshot.docs.length === rentsPageSize);
    } catch (error) {
      console.error("매물 불러오기 실패:", error);
    } finally {
      setIsRentsLoading(false);
    }
  }, [isRentsLoading, lastRentDoc, rentsPageSize]);

  const handleEdit = (rent) => {
    setForm({
      id: rent.id,
      images: rent.images || [],
      title: rent.title || "",
      postalCode: rent.postalCode || "",
      postalCodeCoords: rent.postalCodeCoords || null,
      price: rent.price || "",
      gender: rent.gender || "",
      date: rent.date || "",
      description: rent.description || "",
      category: rent.category || "",
      area: rent.area || "",
      city: rent.city || "",
      bedrooms: rent.bedrooms ?? "",
      bathrooms: rent.bathrooms ?? "",
      parkingSpots: rent.parkingSpots ?? "",
      parkingAvailable: !!rent.parkingAvailable,
      furnished: !!rent.furnished,
      utilitiesIncluded: !!rent.utilitiesIncluded,
      internetIncluded: !!rent.internetIncluded,
      petFriendly: !!rent.petFriendly,
      createdAt: rent.createdAt || null,
      uid: rent.uid || "",
    });
    setShowForm(true);
    // 수정 폼으로 스크롤 (약간의 딜레이를 주어 렌더링 후 스크롤)
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleDelete = async (id) => {
    const confirmed = await openConfirmModal("정말 삭제하시겠습니까?");
    if (!confirmed) return;
    await deleteDoc(doc(db, "rentals", id));
    logActivity("rental_delete", { rentId: id });
    setLastRentDoc(null);
    setHasMoreRents(true);
    fetchRents({ reset: true });
  };

  const handleRefresh = async (rent) => {
    const now = new Date().getTime();
    const lastRefreshed = rent.lastRefreshedAt?.seconds ? rent.lastRefreshedAt.seconds * 1000 : 0;
    const hoursPassed = (now - lastRefreshed) / (1000 * 60 * 60);

    if (hoursPassed < 24) {
      const hoursLeft = Math.ceil(24 - hoursPassed);
      openInfoModal(`${hoursLeft}시간 후에 다시 갱신할 수 있습니다.`);
      return;
    }

    try {
      await updateDoc(doc(db, "rentals", rent.id), {
        // 등록일은 변경하지 않고 노출갱신 시간만 업데이트
        lastRefreshedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sortTimestamp: serverTimestamp(),
      });
      setLastRentDoc(null);
      setHasMoreRents(true);
      fetchRents({ reset: true });
      openInfoModal("노출이 갱신되었습니다!");
    } catch (error) {
      openInfoModal("갱신 실패: " + error.message, "오류");
    }
  };

  const markAsRented = async (id) => {
    await updateDoc(doc(db, "rentals", id), {
      status: "렌트됨",
      updatedAt: serverTimestamp(),
      sortTimestamp: serverTimestamp(),
    });
    setLastRentDoc(null);
    setHasMoreRents(true);
    fetchRents({ reset: true });
  };

  const handleSearch = () => {
    setFilters({
      category: selectedCategory,
      area,
      city,
      price,
      keyword: search,

      bedrooms,
      bathrooms,
      parkingSpots,
      parkingRequired,
      furnishedOnly,
      utilitiesIncluded,
      internetIncluded,
      petFriendly,
      gender: ['룸렌트/부분렌트','룸메이트','홈스테이(하숙)'].includes(selectedCategory) ? genderFilter : "",
    });
    setCurrentPage(1);
  };

  const resetToHome = () => {
    setSelectedRent(null);
    setShowForm(false);
    setShowMyListings(false);
    setReplyTarget(null);
    setReplyContent("");
    setSelectedCategory("");
    setArea("");
    setCity("");
    setPrice("");
    setSearch("");
    setBedrooms("");
    setBathrooms("");
    setParkingSpots("");
    setParkingRequired(false);
    setFurnishedOnly(false);
    setUtilitiesIncluded(false);
    setInternetIncluded(false);
    setPetFriendly(false);
    setGenderFilter("");
    setShowDetailFilters(false);
    setFilters({
      category: "",
      area: "",
      city: "",
      price: "",
      keyword: "",
      bedrooms: "",
      bathrooms: "",
      parkingSpots: "",
      parkingRequired: false,
      furnishedOnly: false,
      gender: "",
      utilitiesIncluded: false,
      internetIncluded: false,
      petFriendly: false,
    });
    setCurrentPage(1);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const toNumericPrice = (val) => {
    const digitsOnly = (val ?? "").toString().replace(/[^0-9]/g, "");
    return digitsOnly ? Number(digitsOnly) : 0;
  };


  const filtered = useMemo(() => (
    rents
      .filter((r) => {
        if ((r.status || "등록됨") === "비활성") {
          return false;
        }
        const matchesCategory = !filters.category || r.category === filters.category;
        const matchesArea = !filters.area || r.area === filters.area;
        const matchesCity = !filters.city || r.city === filters.city;

        // 대소문자 무시 + 여러 키워드 OR 검색
        let matchesKeyword = true;
        if (filters.keyword) {
          const keywords = filters.keyword.toLowerCase().trim().split(/\s+/);
          const searchableText = [
            (r.title || ""),
            (r.description || ""),
            (r.area || ""),
            (r.city || ""),
            (r.category || "")
          ].join(" ").toLowerCase();

          matchesKeyword = keywords.some(kw => searchableText.includes(kw));
        }

        let matchesPrice = true;
        if (filters.price) {
          const rentPrice = toNumericPrice(r.price);
          if (filters.price.includes("+")) {
            matchesPrice = rentPrice >= parseInt(filters.price.replace("+", ""), 10);
          } else {
            const [min, max] = filters.price.split("-");
            matchesPrice =
              rentPrice >= parseInt(min, 10) && rentPrice <= parseInt(max, 10);
          }
        }

        // ✅ 2차 필터 적용 (모든 카테고리에서 사용 가능)
        const isWholeHome =
          filters.category === "콘도(전체렌트)" ||
          filters.category === "하우스(전체렌트)";

        let matchesSub = true;
        if (filters.bedrooms) {
          matchesSub = matchesSub && Number(r.bedrooms ?? 0) === Number(filters.bedrooms);
        }
        if (filters.bathrooms) {
          matchesSub = matchesSub && Number(r.bathrooms ?? 0) === Number(filters.bathrooms);
        }
        if (filters.parkingSpots) {
          matchesSub =
            matchesSub && Number(r.parkingSpots ?? 0) >= Number(filters.parkingSpots);
        }
        if (filters.furnishedOnly) {
          matchesSub = matchesSub && r.furnished === true;
        }

        // 추가 필터들 (성별/유틸/인터넷/반려동물)
        let matchesExtras = true;
        if (filters.gender) {
          matchesExtras = matchesExtras && r.gender === filters.gender;
        }
        if (filters.utilitiesIncluded) {
          matchesExtras = matchesExtras && r.utilitiesIncluded === true;
        }
        if (filters.internetIncluded) {
          matchesExtras = matchesExtras && r.internetIncluded === true;
        }
        if (filters.petFriendly) {
          matchesExtras = matchesExtras && r.petFriendly === true;
        }
        if (filters.parkingRequired) {
          const hasParking = (r.parkingAvailable === true) || (Number(r.parkingSpots ?? 0) >= 1);
          matchesExtras = matchesExtras && hasParking;
        }

        const isMyListing = showMyListings
          ? (user ? r.uid === user.uid : true) // 로그인 전에는 숨기지 않음
          : true;

        return (
          matchesCategory &&
          matchesArea &&
          matchesCity &&
          matchesKeyword &&
          matchesPrice &&
          matchesSub &&
          matchesExtras &&
          isMyListing
        );
      })
      .sort((a, b) => getSortTimestamp(b) - getSortTimestamp(a))
  ), [
    rents,
    filters,
    showMyListings,
    user,
    getSortTimestamp,
  ]);




  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const formatPrice = (raw) => {
    const digitsOnly = (raw || "").replace(/[^0-9]/g, "");
    if (!digitsOnly) return "";
    const num = Number(digitsOnly);
    return `$${num.toLocaleString()}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "price") {
      setForm((prev) => ({ ...prev, price: formatPrice(value) }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const maxImages = 8;
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const remainingSlots = Math.max(0, maxImages - (form.images?.length || 0));
    
    if (remainingSlots === 0) {
      openInfoModal(`사진은 최대 ${maxImages}장까지 업로드 가능합니다.`);
      return;
    }

    const selectedFiles = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      openInfoModal(`사진은 최대 ${maxImages}장까지 업로드 가능합니다. 추가할 수 있는 개수: ${remainingSlots}장`);
    }

    const normalizeFile = async (file) => {
      const name = file?.name || "";
      const isHeic = file?.type === "image/heic" || file?.type === "image/heif" || name.toLowerCase().endsWith(".heic") || name.toLowerCase().endsWith(".heif");
      if (!isHeic) return file;

      try {
        const converted = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        return new File([blob], name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
      } catch (error) {
        console.error("HEIC 변환 실패:", error);
        openInfoModal(`HEIC 변환에 실패했습니다: ${name}\nJPG 사진으로 다시 시도해주세요.`, "오류");
        return null;
      }
    };

    const normalizedFiles = (await Promise.all(selectedFiles.map(normalizeFile))).filter(Boolean);

    const readers = normalizedFiles.map(file => new Promise(resolve => {
      // 파일 크기 체크
      if (file.size > maxFileSize) {
        openInfoModal(`파일 크기가 5MB를 초과합니다: ${file.name}`);
        resolve(null);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        // 이미지 리사이즈 및 압축
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 1200;
          const maxHeight = 1200;
          let { width, height } = img;

          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // 높은 압축률로 처리 (품질 0.65)
          const compressedData = canvas.toDataURL('image/jpeg', 0.65);
          resolve(compressedData);
        };
        img.src = reader.result;
      };
      reader.onerror = () => {
        openInfoModal(`이미지 읽기 실패: ${file.name}`, "오류");
        resolve(null);
      };
      reader.readAsDataURL(file);
    }));

    Promise.all(readers).then(images => {
      const validImages = images.filter(img => img !== null);
      setForm(prev => ({ ...prev, images: [...(prev.images || []), ...validImages] }));
    });
  };

  const handleRemoveImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
    }));
  };

  const openInfoModal = useCallback((message, title = "안내") => {
    setInfoModalTitle(title);
    setInfoModalMessage(message);
    setShowInfoModal(true);
  }, []);

  const openConfirmModal = useCallback((message, title = "확인") => new Promise((resolve) => {
    confirmResolver.current = resolve;
    setConfirmModalTitle(title);
    setConfirmModalMessage(message);
    setShowConfirmModal(true);
  }), []);

  const handleAccountLinkPrompt = useCallback(async (error) => {
    const email = error?.customData?.email || "";
    const credential = GoogleAuthProvider.credentialFromError(error);
    if (!email || !credential) return false;

    setPendingLinkCredential(credential);
    setPendingLinkEmail(email);

    let methods = [];
    try {
      methods = await fetchSignInMethodsForEmail(auth, email);
    } catch (e) {
      logWarn("[link] fetchSignInMethodsForEmail 실패:", e?.message);
    }

    const methodLabel = methods.length
      ? methods.map(providerLabel).join(", ")
      : "기존 로그인";

    openInfoModal(
      `${email}은 이미 ${methodLabel}로 가입되어 있습니다.\n\n` +
      `해당 방식으로 로그인하면 자동으로 Google 계정이 연동됩니다.`,
      "계정 연동"
    );
    setShowSignupModal(false);
    setShowLoginModal(true);
    setIsLoggingIn(false);
    return true;
  }, [openInfoModal]);

  const resolveConfirmModal = useCallback((confirmed) => {
    setShowConfirmModal(false);
    if (confirmResolver.current) {
      confirmResolver.current(confirmed);
      confirmResolver.current = null;
    }
  }, []);

  const logActivity = async (action, meta = {}) => {
    if (!user?.uid) return;
    try {
      await addDoc(collection(db, "activityLogs"), {
        action,
        uid: user.uid,
        email: user.email || null,
        createdAt: serverTimestamp(),
        ...meta,
      });
    } catch (e) {
      logWarn("[logActivity] 실패:", e);
    }
  };

  const logLogin = useCallback(async (targetUser, action, meta = {}) => {
    if (!targetUser?.uid) return;
    try {
      await addDoc(collection(db, "loginLogs"), {
        action,
        uid: targetUser.uid,
        email: targetUser.email || null,
        provider: targetUser.providerData?.[0]?.providerId || null,
        result: "success",
        createdAt: serverTimestamp(),
        ...meta,
      });
    } catch (e) {
      logWarn("[logLogin] 실패:", e);
    }
  }, []);

  const closeNoticePopup = () => {
    if (noticePopup?.id && user?.uid) {
      const storageKey = `notice_seen_${user.uid}_${noticePopup.id}`;
      try {
        localStorage.setItem(storageKey, "true");
      } catch (_) {
        // localStorage 접근 불가 시 무시
      }
    }
    setShowNoticePopup(false);
    setNoticePopup(null);
  };

  const formatDateOnly = (value) => {
    if (!value) return "";
    const date = value?.seconds
      ? new Date(value.seconds * 1000)
      : value instanceof Date
        ? value
        : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  const formatTimestamp = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value?.toDate) {
      return value.toDate().toLocaleString();
    }
    const date = value?.seconds
      ? new Date(value.seconds * 1000)
      : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
  };

  const resetEmailCollectState = () => {
    setShowEmailCollectModal(false);
    setEmailCollectValue("");
    setEmailCollectError("");
    setEmailCollectUser(null);
    setEmailCollectProvider("");
  };

  const handleCloseDuplicateModal = useCallback(() => {
    blockAuthSync.current = false;
    setShowDuplicateModal(false);
  }, []);

  const handleCloseDuplicateAndOpenLogin = useCallback(() => {
    blockAuthSync.current = false;
    setShowDuplicateModal(false);
    setShowLoginModal(true);
  }, []);

  const handleCancelEmailCollect = () => {
    if (emailCollectResolver.current) {
      emailCollectResolver.current("");
      emailCollectResolver.current = null;
    }
    clearPendingKakaoLink();
    resetEmailCollectState();
  };

  const ensureSocialEmail = useCallback(async (firebaseUser, provider, fallbackEmail = "") => {
    logDebug("[ensureSocialEmail] 호출됨 - provider:", provider, "user uid:", firebaseUser?.uid);
    
    const existingEmail = fallbackEmail
      || firebaseUser?.email
      || firebaseUser?.providerData?.find((p) => p?.email)?.email;

    logDebug("[ensureSocialEmail] fallbackEmail:", fallbackEmail);
    logDebug("[ensureSocialEmail] firebaseUser.email:", firebaseUser?.email);
    logDebug("[ensureSocialEmail] providerData email:", firebaseUser?.providerData?.find((p) => p?.email)?.email);
    logDebug("[ensureSocialEmail] existingEmail:", existingEmail);

    if (existingEmail) {
      logDebug("[ensureSocialEmail] 이메일 이미 있음, 모달 안 보임:", existingEmail);
      return existingEmail;
    }

    logDebug("[ensureSocialEmail] 이메일 없음, 모달 표시");
    return new Promise((resolve) => {
      emailCollectResolver.current = resolve;
      setEmailCollectUser(firebaseUser || null);
      setEmailCollectProvider(provider || "social");
      setEmailCollectValue("");
      setEmailCollectError("");
      setShowEmailCollectModal(true);
      logDebug("[ensureSocialEmail] 모달 상태 설정 완료");
    });
  }, [
    setShowEmailCollectModal,
    setEmailCollectUser,
    setEmailCollectProvider,
    setEmailCollectValue,
    setEmailCollectError,
  ]);

  const handleSaveCollectedEmail = async () => {
    const trimmed = emailCollectValue.trim();
    if (!trimmed) {
      setEmailCollectError("이메일을 입력해주세요.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setEmailCollectError("이메일 형식이 올바르지 않습니다.");
      return;
    }

    try {
      if (emailCollectUser?.uid) {
        await setDoc(doc(db, "users", emailCollectUser.uid), {
          email: trimmed,
          provider: emailCollectProvider || null,
          updatedAt: new Date().toISOString(),
        }, { merge: true });
      }

      if (emailCollectResolver.current) {
        emailCollectResolver.current(trimmed);
        emailCollectResolver.current = null;
      }
      resetEmailCollectState();
    } catch (error) {
      console.error("이메일 저장 실패:", error);
      setEmailCollectError("이메일 저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const openLightbox = (images, index = 0) => {
    if (!images || images.length === 0) return;
    setLightbox({ images, index });
  };

  const closeLightbox = () => setLightbox(null);

  const moveLightbox = (direction) => {
    setLightbox(prev => {
      if (!prev) return prev;
      const total = prev.images.length;
      const nextIndex = (prev.index + direction + total) % total;
      return { ...prev, index: nextIndex };
    });
  };

  const handleMoveImage = (fromIndex, direction) => {
    setForm(prev => {
      const imgs = [...(prev.images || [])];
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= imgs.length) return prev;
      [imgs[fromIndex], imgs[toIndex]] = [imgs[toIndex], imgs[fromIndex]];
      return { ...prev, images: imgs };
    });
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (index) => {
    setForm(prev => {
      if (dragIndex === null || dragIndex === index) return prev;
      const imgs = [...(prev.images || [])];
      const [moved] = imgs.splice(dragIndex, 1);
      imgs.splice(index, 0, moved);
      return { ...prev, images: imgs };
    });
    setDragIndex(null);
  };

  const handleSubmit = async () => {
    if (!user) {
      openInfoModal("로그인 후 렌트 매물 등록이 가능합니다.");
      return;
    }
    const isRentRequest = isRentRequestCategory(form.category);
    const validation = validateRentalForm(form, isRentRequest);
    if (!validation.isValid) {
      openInfoModal("* 표시는 필수 입력 항목입니다.");
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(false);
    try {
      const newForm = {
        ...form,
        createdAt: form.id ? form.createdAt : serverTimestamp(), // 수정 시 기존 createdAt 유지
        updatedAt: serverTimestamp(),
        uid: user?.uid,
        status: form.status || "등록됨",

        // ✅ 숫자/boolean으로 저장
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        parkingSpots: form.parkingSpots === "" ? null : Number(form.parkingSpots),
        parkingAvailable: !!form.parkingAvailable,
        furnished: !!form.furnished,
        utilitiesIncluded: !!form.utilitiesIncluded,
        internetIncluded: !!form.internetIncluded,
        petFriendly: !!form.petFriendly,
        postalCodeCoords: form.postalCodeCoords || null,
        // postalCode는 저장하지 않음 (개인정보 보호)
      };
      
      // 새 등록일 때만 lastRefreshedAt 설정
      if (!form.id) {
        newForm.lastRefreshedAt = serverTimestamp();
      }
      newForm.sortTimestamp = serverTimestamp();
      
      if (form.id) {
        await setDoc(doc(db, "rentals", form.id), newForm, { merge: true });
        logActivity("rental_update", { rentId: form.id, rentTitle: form.title || "" });
      } else {
        const docRef = await addDoc(collection(db, "rentals"), newForm);
        logActivity("rental_create", { rentId: docRef.id, rentTitle: form.title || "" });
      }
      const editedId = form.id; // 수정 시 해당 ID 기억
      setLastRentDoc(null);
      setHasMoreRents(true);
      await fetchRents({ reset: true });
      // 수정한 매물이 selectedRent로 열려있으면 최신 데이터로 갱신
      if (editedId) {
        setSelectedRent(prev => {
          if (prev?.id === editedId) {
            return { ...prev, ...newForm, id: editedId };
          }
          return prev;
        });
      }
      setForm({ images: [], title: "", postalCode: "", postalCodeCoords: null, price: "", gender: "", date: "", description: "", category: "", area: "", city: "", bedrooms: "", bathrooms: "", parkingSpots: "", parkingAvailable: false, furnished: false, utilitiesIncluded: false, internetIncluded: false, petFriendly: false });
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setShowForm(false);
        setIsSubmitting(false);
      }, 2000);
    } catch (error) {
      openInfoModal("등록 실패: " + error.message, "오류");
      setSubmitSuccess(false);
      setIsSubmitting(false);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) return;
    setIsLoggingIn(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      logDebug("[handleLogin] Email:", userCredential.user.email);
      logDebug("[handleLogin] ProviderData:", userCredential.user.providerData);

      // 이메일 인증 상태 최신화
      try {
        await userCredential.user.reload();
      } catch (e) {
        logWarn("[handleLogin] user reload 실패:", e);
      }
      const verifiedUser = auth.currentUser || userCredential.user;
      
      // 이메일 인증 완료 확인
      if (!verifiedUser.emailVerified) {
        await signOut(auth);
        openInfoModal("이메일 인증이 필요합니다.\n\n회원가입 시 받으신 인증 이메일의 링크를 클릭해주세요.");
        return;
      }

      const pendingKakao = loadPendingKakaoLink();
      if (pendingKakao?.accessToken && KAKAO_LINK_ENDPOINT) {
        const createdAt = Number(pendingKakao.createdAt || 0);
        const ageMs = createdAt ? Date.now() - createdAt : 0;
        if (!createdAt || ageMs > 30 * 60 * 1000) {
          clearPendingKakaoLink();
        } else {
          const confirmed = await openConfirmModal(
            "이 계정에 카카오 로그인을 연동할까요?\n연동하면 다음부터 카카오로 바로 로그인할 수 있습니다.",
            "계정 연동"
          );
          if (!confirmed) {
            clearPendingKakaoLink();
          } else {
            try {
              const idToken = await verifiedUser.getIdToken();
              const resp = await fetch(KAKAO_LINK_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ idToken, access_token: pendingKakao.accessToken }),
              });

              if (resp.ok) {
                clearPendingKakaoLink();
                openInfoModal("카카오 계정이 연동되었습니다.", "계정 연동");
              } else {
                const raw = await resp.text();
                logWarn("[kakao link] 실패:", raw);
                openInfoModal("카카오 계정 연동에 실패했습니다. 다시 시도해주세요.", "오류");
                clearPendingKakaoLink();
              }
            } catch (linkErr) {
              logWarn("[kakao link] 오류:", linkErr?.message || linkErr);
              openInfoModal("카카오 계정 연동 중 오류가 발생했습니다.", "오류");
              clearPendingKakaoLink();
            }
          }
        }
      }

      if (pendingLinkCredential && pendingLinkEmail) {
        const verifiedEmail = (verifiedUser.email || "").toLowerCase();
        if (verifiedEmail && verifiedEmail === pendingLinkEmail.toLowerCase()) {
          try {
            await linkWithCredential(verifiedUser, pendingLinkCredential);
            openInfoModal("계정이 성공적으로 연동되었습니다.", "계정 연동");
          } catch (linkError) {
            openInfoModal("계정 연동 실패: " + (linkError?.message || "알 수 없는 오류"), "오류");
          } finally {
            setPendingLinkCredential(null);
            setPendingLinkEmail("");
          }
        } else {
          setPendingLinkCredential(null);
          setPendingLinkEmail("");
        }
      }

      setUser(verifiedUser);
      const adminFlag = isAdminUser(verifiedUser);
      logDebug("[DEBUG] handleLogin success: adminFlag =", adminFlag, "user:", verifiedUser.email);
      setPendingEmailVerification(false);
      setShowEmailVerificationModal(false);
      logLogin(verifiedUser, "login_email");
      setIsAdmin(adminFlag);
      setShowAdminPage(adminFlag);
      setShowLoginModal(false);
    } catch (error) {
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        setLoginHelpMessage("등록되지 않아거나 아이디/비밀번호가 올라르지 않습니다.\n\n다시 확인해 주세요.");
        setShowLoginModal(false);
        setShowLoginHelpModal(true);
      } else {
        openInfoModal("로그인 실패: " + error.message, "오류");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    try {
      localStorage.setItem('krent_last_email', value);
      setLastEmail(value);
    } catch (_) {
      // localStorage가 막혀있을 수 있으므로 무시
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setShowLoginModal(false);
      openInfoModal("비밀번호 재설정 이메일을 받으려면 이메일을 입력해주세요.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setShowLoginModal(false);
      openInfoModal(
        "비밀번호 재설정 링크를 이메일로 보냈습니다.\n\n" +
        "새 비밀번호 규칙:\n\n" +
        "• 8자 이상 / 64자 이하\n" +
        "• 대문자·소문자·숫자·특수문자 중 3가지 이상 포함\n" +
        "• 공백/쉬운 패턴/이메일과 유사한 비밀번호 금지\n\n" +
        "규칙에 부합하지 않으면 재설정이 거부됩니다."
      );
    } catch (error) {
      setShowLoginModal(false);
      openInfoModal("비밀번호 재설정 실패: " + error.message, "오류");
    }
  };

  // 회원가입 모달에서 약관 동의 후에만 소셜 가입 허용
  const ensureKakaoSDK = useCallback(async () => {
    // 환경 변수 확인 및 로깅
    logDebug("=== Kakao SDK 초기화 시작 ===");
    logDebug("KAKAO_JS_KEY:", KAKAO_JS_KEY ? `${KAKAO_JS_KEY.substring(0, 10)}...` : "없음");
    
    if (!KAKAO_JS_KEY) {
      throw new Error("Kakao JS 키가 설정되어 있지 않습니다. REACT_APP_KAKAO_JS_KEY 환경변수를 설정하세요.");
    }

    // 이미 초기화되어 있으면 바로 반환
    if (window.Kakao && window.Kakao.isInitialized && window.Kakao.isInitialized()) {
      logDebug("이미 초기화됨");
      return;
    }

    // 이미 스크립트가 로드되어 있는지 확인
    const existingScript = document.querySelector('script[src*="kakao"]');
    
    if (!existingScript) {
      logDebug("SDK 스크립트 로드 중...");
      // 새로 스크립트 로드
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js";
        script.integrity = "sha384-TiCUE00h649CAMonG018J2ujOgDKW/kVWlChEuu4jK2vxfAAD0eZxzCKakxg55G4";
        script.crossOrigin = "anonymous";
        script.async = true;
        script.onload = () => {
          logDebug("✅ Kakao SDK 로드 완료");
          resolve();
        };
        script.onerror = () => reject(new Error("Kakao SDK 로드 실패"));
        document.head.appendChild(script);
      });

      // SDK 로드 대기 (최대 3초)
      let attempts = 0;
      while (!window.Kakao && attempts < 30) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
    }

    if (!window.Kakao) {
      throw new Error("Kakao SDK가 로드되지 않았습니다.");
    }

    // 초기화 (중복 초기화 방지)
    if (!window.Kakao.isInitialized()) {
      logDebug("SDK 초기화 중... 키:", KAKAO_JS_KEY.substring(0, 10) + "...");
      try {
        window.Kakao.init(KAKAO_JS_KEY);
        logDebug("✅ Kakao SDK 초기화 완료:", window.Kakao.isInitialized());
      } catch (error) {
        console.error("❌ Kakao SDK 초기화 실패:", error);
        throw new Error("Kakao SDK 초기화 실패: " + error.message + ". JavaScript 키를 확인하세요.");
      }
    }

    // Auth 모듈 확인
    if (!window.Kakao.Auth) {
      throw new Error("Kakao Auth 모듈을 사용할 수 없습니다. SDK 버전을 확인하세요.");
    }
    
    logDebug("✅ Kakao SDK 준비 완료");
    logDebug("=========================");
  }, [KAKAO_JS_KEY]);

  const isMobileOrInAppBrowser = useCallback(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    const isInApp = /KAKAOTALK|FBAN|FBAV|Instagram|Line|NAVER|Daum|Whale/i.test(ua);
    return isMobile || isInApp;
  }, []);


  const isKakaoTalkInApp = () => {
    if (typeof navigator === "undefined") return false;
    return /KAKAOTALK|KakaoTalk/i.test(navigator.userAgent || "");
  };

  const kakaoLoginViaSdk = () => new Promise((resolve, reject) => {
    window.Kakao.Auth.login({
      scope: "profile_nickname,profile_image",
      throughTalk: false,
      success: resolve,
      fail: reject,
    });
  });

  // Mobile/in-app policy: KakaoTalk uses SDK login, others use redirect.
  const shouldUseKakaoSdkLogin = () => isKakaoTalkInApp();

  const handleKakaoLogin = async () => {
    setIsLoggingIn(true);
    try {
      await ensureKakaoSDK();

      // 카카오 SDK 준비 상태 재확인
      if (!window.Kakao || !window.Kakao.Auth) {
        throw new Error("Kakao SDK가 준비되지 않았습니다. 페이지를 새로고침하세요.");
      }

      logDebug("카카오 로그인 시작...");

      // 로그인 시에는 회원가입 플래그 제거
      localStorage.removeItem('kakao_signup_mode');

      if (shouldUseKakaoSdkLogin()) {
        logDebug("카카오톡 인앱: SDK 로그인 사용");
        try {
          await kakaoLoginViaSdk();
          await handleKakaoLoginCallback({ isSignupOverride: false });
          return;
        } catch (sdkError) {
          logWarn("카카오 SDK 로그인 실패, 리다이렉트로 폴백", sdkError);
        }
      }

      // 리다이렉트 방식 사용 (가장 안정적)
      window.Kakao.Auth.authorize({
        redirectUri: getKakaoRedirectUri(),
        scope: 'profile_nickname,profile_image',
        throughTalk: false,
        state: 'login',
      });
    } catch (error) {
      console.error("카카오 로그인 에러:", error);
      setAuthError("카카오 로그인 실패: " + (error?.message || "알 수 없는 오류"));
      setIsLoggingIn(false);
    }
  };

  const handleKakaoLoginCallback = useCallback(async (authObj) => {
    setIsLoggingIn(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const stateParam = params.get("state");
      const localFlag = localStorage.getItem('kakao_signup_mode');
      logDebug("[Kakao Callback] stateParam:", stateParam, "localFlag:", localFlag);
      // signup으로 명시적으로 지정된 경우만 회원가입 모드
      const isSignupOverride = typeof authObj?.isSignupOverride === "boolean"
        ? authObj.isSignupOverride
        : null;
      const isSignup = typeof isSignupOverride === "boolean"
        ? isSignupOverride
        : (stateParam === "signup" && localFlag === 'true');
      logDebug("[Kakao Callback] isSignup:", isSignup);
      if (isSignup) {
        blockAuthSync.current = true;
      }

      const accessToken = window.Kakao.Auth.getAccessToken();
      if (!accessToken) {
        throw new Error("카카오 액세스 토큰을 가져오지 못했습니다.");
      }

      logDebug("Access Token 획득:", accessToken.substring(0, 10) + "...");

      // 사용자 정보 조회
      const me = await window.Kakao.API.request({ url: "/v2/user/me" });
      logDebug("사용자 정보:", me);

      if (!KAKAO_CUSTOM_TOKEN_ENDPOINT) {
        throw new Error("커스텀 토큰 엔드포인트가 설정되어 있지 않습니다. REACT_APP_KAKAO_CUSTOM_TOKEN_ENDPOINT 환경변수를 설정하세요.");
      }

      // 서버에 액세스 토큰 전달하여 Firebase 커스텀 토큰 발급
      if (!me?.kakao_account?.email) {
        savePendingKakaoLink({
          accessToken,
          kakaoId: me?.id || null,
          createdAt: Date.now(),
        });
      } else {
        clearPendingKakaoLink();
      }

      const res = await fetch(KAKAO_CUSTOM_TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_token: accessToken, kakao_user_id: me?.id, isSignup }),
      });

      if (!res.ok) {
        const raw = await res.text();
        let message = raw;
        try {
          const parsed = JSON.parse(raw);
          message = parsed?.message || parsed?.error || raw;
        } catch (_) {}

        if (res.status === 403 && `${message}`.toLowerCase().includes("user not found")) {
          openInfoModal("계정을 찾을 수 없습니다. 회원가입 후 이용해주세요.", "안내");
        } else {
          openInfoModal("카카오 로그인에 실패했습니다. 잠시 후 다시 시도해주세요.", "오류");
        }
        try { window?.Kakao?.Auth?.logout(); } catch (_) {}
        window.history.replaceState({}, document.title, window.location.pathname);
        localStorage.removeItem('kakao_signup_mode');
        return;
      }

      const { customToken } = await res.json();
      if (!customToken) {
        throw new Error("서버에서 커스텀 토큰을 반환하지 않았습니다.");
      }

      const userCredential = await signInWithCustomToken(auth, customToken);

      // 회원가입 모드에서 기존 계정이면 즉시 차단 (메타데이터 + Firestore 둘 다 확인)
      if (isSignup) {
        const meta = userCredential?.user?.metadata;
        const isExistingByMeta = meta?.creationTime && meta?.lastSignInTime && meta.creationTime !== meta.lastSignInTime;

        let isExistingByDoc = false;
        try {
          const existingRef = doc(db, "users", userCredential.user.uid);
          const existingDoc = await getDoc(existingRef);
          isExistingByDoc = existingDoc.exists();
        } catch (readErr) {
          logWarn("기존 사용자 문서 확인 실패", readErr);
        }

        if (isExistingByMeta || isExistingByDoc) {
          logDebug("[Callback] 기존 계정 감지 - meta:", isExistingByMeta, "doc:", isExistingByDoc);

          // alert 전에 먼저 로그아웃
          blockAuthSync.current = true;
          await signOut(auth);

          // Kakao 로그아웃
          try {
            if (window.Kakao?.Auth) {
              window.Kakao.Auth.logout();
            }
          } catch (e) {
            logDebug("카카오 로그아웃 중 오류:", e);
          }

          setUser(null);
          setShowSignupModal(false);
          setIsAdmin(false);
          setShowAdminPage(false);
          window.history.replaceState({}, document.title, window.location.pathname);
          localStorage.removeItem('kakao_signup_mode');

          blockAuthSync.current = false;

          // UI가 로그아웃된 상태로 렌더된 뒤 안내 모달 → 확인 후 로그인 모달
          setShowDuplicateModal(true);
          logDebug("[Callback] 회원가입 차단 완료, 로그인 모달 열림");
          return;
        }
      }

      setUser(userCredential.user);
      const adminFlag = isAdminUser(userCredential.user);
      setIsAdmin(adminFlag);
      setShowAdminPage(adminFlag);
      setPendingEmailVerification(false);
      setShowEmailVerificationModal(false);
      const { uid } = userCredential.user;
      logDebug("Firebase 로그인 성공:", uid);
      blockAuthSync.current = false;
      logLogin(userCredential.user, isSignup ? "signup_kakao" : "login_kakao");

      // 로그인 모달 닫기
      setShowLoginModal(false);
      setShowSignupModal(false);
      setAuthError("");
      localStorage.removeItem('kakao_signup_mode');

      // Firestore에 Kakao 프로필 동기화
      try {
        const { uid } = userCredential.user;
        const kakaoId = me?.id || null;
        const email = me?.kakao_account?.email || userCredential.user.email || null;
        const nickname = me?.kakao_account?.profile?.nickname || me?.properties?.nickname || null;
        const photoURL = me?.kakao_account?.profile?.profile_image_url || me?.properties?.profile_image || null;

        const userDocRef = doc(db, "users", uid);
        const userDoc = await getDoc(userDocRef);

        // 신규 사용자면 createdAt 포함, 기존 사용자면 업데이트만
        const userData = {
          uid,
          provider: "kakao",
          kakaoId,
          email: email || null,
          displayName: nickname,
          photoURL,
          updatedAt: new Date().toISOString(),
        };
        
        if (!userDoc.exists()) {
          userData.createdAt = new Date().toISOString();
        }

        await setDoc(userDocRef, userData, { merge: true });
        logDebug("사용자 정보 저장 완료");

        // Firestore 저장 후 이메일이 없으면 모달 표시
        if (!email) {
          logDebug("[Kakao] 이메일 없음, 모달 표시");
          setTimeout(() => {
            ensureSocialEmail(userCredential.user, "kakao", null).catch(() => {});
          }, 300);
        }
      } catch (e) {
        logWarn("사용자 프로필 저장 실패", e);
      }
    } catch (error) {
      console.error("카카오 로그인 콜백 에러:", error);
      console.error("에러 코드:", error?.code);
      console.error("에러 메시지:", error?.message);
      
      // 네트워크 오류인 경우 안내
      if (error?.code === 'auth/network-request-failed' || error?.message?.includes('network')) {
        setAuthError("네트워크 연결을 확인해주세요. 인터넷이 정상인지 확인 후 다시 시도해주세요.");
      } else {
        setAuthError("카카오 로그인 실패: " + (error?.message || "알 수 없는 오류"));
      }
      
      // 로그인 실패 시 토큰 삭제
      if (window.Kakao) {
        try {
          window.Kakao.Auth.logout();
        } catch (e) {
          logDebug("로그아웃 중 오류:", e);
        }
      }
    } finally {
      setIsLoggingIn(false);
    }
  }, [
    KAKAO_CUSTOM_TOKEN_ENDPOINT,
    ensureSocialEmail,
    logLogin,
    openInfoModal,
  ]);

  const handleKakaoSignup = async () => {
    if (!agreeTerms) {
      setShowInfoModal(false);
      setShowTermsModal(true);
      return;
    }
    
    setIsLoggingIn(true);
    try {
      await ensureKakaoSDK();

      if (!window.Kakao || !window.Kakao.Auth) {
        throw new Error("Kakao SDK가 준비되지 않았습니다. 페이지를 새로고침하세요.");
      }

      logDebug("카카오 회원가입 시작...");

      // localStorage에 signup 플래그 저장
      localStorage.setItem('kakao_signup_mode', 'true');

      if (shouldUseKakaoSdkLogin()) {
        logDebug("카카오톡 인앱: SDK 회원가입 로그인 사용");
        try {
          await kakaoLoginViaSdk();
          await handleKakaoLoginCallback({ isSignupOverride: true });
          return;
        } catch (sdkError) {
          logWarn("카카오 SDK 회원가입 로그인 실패, 리다이렉트로 폴백", sdkError);
        }
      }

      // 리다이렉트 방식 사용
      window.Kakao.Auth.authorize({
        redirectUri: getKakaoRedirectUri(),
        scope: 'profile_nickname,profile_image',
        throughTalk: false,
        state: 'signup',
      });
    } catch (error) {
      console.error("카카오 회원가입 에러:", error);
      setAuthError("카카오 회원가입 실패: " + (error?.message || "알 수 없는 오류"));
      setIsLoggingIn(false);
    }
  };

  const canUseGooglePopup = () => {
    if (typeof window === "undefined") return false;
    // 모바일에서도 팝업을 먼저 시도합니다 (실패하면 자동으로 리다이렉트 폴백)
    return true;
  };

  const shouldFallbackToRedirect = (code = "") => (
    code === "auth/popup-blocked"
    || code === "auth/operation-not-supported-in-this-environment"
    || code === "auth/cancelled-popup-request"
  );

  const handleGoogleAuthError = useCallback((error, { isSignup }) => {
    if (error?.code === "auth/account-exists-with-different-credential") {
      handleAccountLinkPrompt(error)
        .then((handled) => {
          if (handled) return;
          console.error(isSignup ? "Google 회원가입 에러:" : "Google 로그인 에러:", error);
          setAuthError(`Google ${isSignup ? "회원가입" : "로그인"} 실패: ${error?.message || "알 수 없는 오류"}`);
          blockAuthSync.current = false;
          localStorage.removeItem('google_signup_mode');
          setIsLoggingIn(false);
        })
        .catch(() => {
          blockAuthSync.current = false;
          localStorage.removeItem('google_signup_mode');
          setIsLoggingIn(false);
        });
      return;
    }
    console.error(isSignup ? "Google 회원가입 에러:" : "Google 로그인 에러:", error);
    setAuthError(`Google ${isSignup ? "회원가입" : "로그인"} 실패: ${error?.message || "알 수 없는 오류"}`);
    blockAuthSync.current = false;
    localStorage.removeItem('google_signup_mode');
    setIsLoggingIn(false);
  }, [handleAccountLinkPrompt]);

  const finalizeGoogleAuth = useCallback(async (userCredential, options = {}) => {
    if (!userCredential?.user) {
      logDebug("[finalizeGoogleAuth] userCredential.user 없음, 종료");
      setIsLoggingIn(false);
      return;
    }

    logDebug("[finalizeGoogleAuth] 시작:", userCredential.user.email);

    const { isSignupModeOverride = null } = options;
    const googleSignupMode = typeof isSignupModeOverride === "boolean"
      ? isSignupModeOverride
      : localStorage.getItem('google_signup_mode') === 'true';
    
    // 회원가입 모드 중복 판단은 서버 검증 결과만 사용합니다.

    const info = getAdditionalUserInfo(userCredential);
    const isNewUser = info?.isNewUser === true;
    let resolvedEmail = userCredential.user.email;
    if (!resolvedEmail) {
      logDebug("[finalizeGoogleAuth] 이메일 없음");
    }

    const idToken = await userCredential.user.getIdToken();
    let verificationPassed = true;
    try {
      logDebug("[finalizeGoogleAuth] 서버 검증 시작");
      const verifyResp = await fetch(VERIFY_EXISTING_USER_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      if (!verifyResp.ok) {
        const message = await verifyResp.text();
        throw new Error(message || `HTTP ${verifyResp.status}`);
      }

      let verifyData = await verifyResp.json();
      logDebug("[finalizeGoogleAuth] 검증 결과:", verifyData);

      if (googleSignupMode && verifyData?.allowed === false) {
        logDebug("[finalizeGoogleAuth] 서버 검증: 기존 사용자 감지됨 - 중복 가입 차단");
        await signOut(auth);
        setUser(null);
        setShowDuplicateModal(true);
        setShowSignupModal(false);
        localStorage.removeItem('google_signup_mode');
        blockAuthSync.current = false;
        setIsLoggingIn(false);

        if (window.location.search || window.location.hash) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        return;
      }

      if (!verifyData?.allowed) {
        let userDocCreated = false;
        try {
          logDebug("[finalizeGoogleAuth] 사용자 문서 생성 시도");
          await setDoc(doc(db, "users", userCredential.user.uid), {
            uid: userCredential.user.uid,
            provider: "google",
            email: resolvedEmail || null,
            displayName: userCredential.user.displayName,
            photoURL: userCredential.user.photoURL,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }, { merge: true });
          userDocCreated = true;
          logDebug("[finalizeGoogleAuth] 사용자 문서 생성 성공");
        } catch (createErr) {
          logWarn("자동 사용자 문서 생성 실패", createErr);
        }

        if (userDocCreated) {
          const retryResp = await fetch(VERIFY_EXISTING_USER_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ idToken }),
          });
          const retryData = await retryResp.json();
          logDebug("[finalizeGoogleAuth] 재검증 결과:", retryData);
          if (!retryData?.allowed) {
            throw new Error("재검증 실패");
          }
        } else {
          throw new Error("사용자 문서를 생성할 수 없습니다");
        }
      }
    } catch (error) {
      verificationPassed = false;
      if (googleSignupMode) {
        logWarn("[finalizeGoogleAuth] 회원가입 모드 검증 실패 - 가입 중단:", error);
        await signOut(auth);
        setUser(null);
        setShowSignupModal(false);
        localStorage.removeItem('google_signup_mode');
        blockAuthSync.current = false;
        openInfoModal("서버 검증에 실패했습니다. 잠시 후 다시 시도해주세요.", "안내");
        setIsLoggingIn(false);

        if (window.location.search || window.location.hash) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        return;
      }
      logWarn("[finalizeGoogleAuth] 검증 실패, 로그인은 진행합니다:", error);
    }

    logDebug("[finalizeGoogleAuth] 상태 업데이트 시작");
    blockAuthSync.current = false;
    setUser(userCredential.user);
    const adminFlag = isAdminUser(userCredential.user);
    setIsAdmin(adminFlag);
    setShowAdminPage(adminFlag);
    setPendingEmailVerification(false);
    setShowEmailVerificationModal(false);
    setAgreeTerms(true);
    setShowTermsModal(false);
    setShowInfoModal(false);
    setShowLoginModal(false);
    setShowSignupModal(false);
    setAuthError("");
    localStorage.removeItem('google_signup_mode');

    try {
      await setDoc(doc(db, "users", userCredential.user.uid), {
        uid: userCredential.user.uid,
        provider: "google",
        email: resolvedEmail || null,
        displayName: userCredential.user.displayName,
        photoURL: userCredential.user.photoURL,
        updatedAt: new Date().toISOString(),
      }, { merge: true });
      logDebug("[finalizeGoogleAuth] Firestore 업데이트 완료");
    } catch (e) {
      logWarn("사용자 프로필 저장 실패", e);
    }

    logLogin(userCredential.user, isNewUser ? "login_google_signup" : "login_google");
    logDebug("[finalizeGoogleAuth] logLogin 완료");

    if (!verificationPassed) {
      openInfoModal("서버 검증에 실패했지만 로그인은 완료되었습니다. 문제가 지속되면 관리자에게 문의해주세요.", "안내");
    }

    if (window.location.search || window.location.hash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // 신규 사용자이면 이메일 모달 표시 (로그인 후)
    if (isNewUser) {
      logDebug("[finalizeGoogleAuth] 신규 사용자, 이메일 모달 표시");
      setTimeout(() => {
        ensureSocialEmail(userCredential.user, "google", resolvedEmail).catch(() => {});
      }, 300);
    }

    logDebug("[finalizeGoogleAuth] 완료");
    setIsLoggingIn(false);
  }, [ensureSocialEmail, logLogin, openInfoModal, VERIFY_EXISTING_USER_ENDPOINT]);

  const initiateGoogleAuth = useCallback(async ({ isSignup }) => {
    try {
      if (isMobileOrInAppBrowser()) {
        const confirmed = await openConfirmModal(
          "구글 이메일 인증 후 krent 사이트로 돌아와 주세요.",
          "안내"
        );
        if (!confirmed) {
          return;
        }
      }

      setIsLoggingIn(true);
      if (isSignup) {
        localStorage.setItem('google_signup_mode', 'true');
        logDebug("[initiateGoogleAuth] 회원가입 모드 시작");
      } else {
        localStorage.removeItem('google_signup_mode');
        logDebug("[initiateGoogleAuth] 로그인 모드 시작");
      }

      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      blockAuthSync.current = true;

      if (canUseGooglePopup()) {
        logDebug("[initiateGoogleAuth] 팝업 시도");
        try {
          const popupResult = await signInWithPopup(auth, provider);
          logDebug("[initiateGoogleAuth] 팝업 성공:", popupResult.user?.email);
          await finalizeGoogleAuth(popupResult, { isSignupModeOverride: isSignup });
          logDebug("[initiateGoogleAuth] finalizeGoogleAuth 완료");
          return;
        } catch (popupError) {
          logWarn("[initiateGoogleAuth] 팝업 에러:", popupError?.code, popupError?.message);
          if (shouldFallbackToRedirect(popupError?.code)) {
            logWarn("[initiateGoogleAuth] 팝업 실패, 리다이렉트 시도:", popupError?.code);
          } else {
            console.error("[initiateGoogleAuth] 팝업 에러(폴백 불가):", popupError?.message);
            handleGoogleAuthError(popupError, { isSignup });
            return;
          }
        }
      } else {
        logDebug("[initiateGoogleAuth] 팝업 불가 환경, 리다이렉트 시도");
      }

      logDebug("[initiateGoogleAuth] signInWithRedirect 호출");
      await signInWithRedirect(auth, provider);
      logDebug("[initiateGoogleAuth] signInWithRedirect 완료 (리다이렉트 진행 중)");
    } catch (error) {
      console.error("[initiateGoogleAuth] 최상단 catch, 에러:", error?.message);
      handleGoogleAuthError(error, { isSignup });
    }
  }, [finalizeGoogleAuth, handleGoogleAuthError, isMobileOrInAppBrowser, openConfirmModal]);


  const handleGoogleSignup = async () => {
    if (!agreeTerms) {
      setShowInfoModal(false);
      setShowTermsModal(true);
      return;
    }
    initiateGoogleAuth({ isSignup: true });
  };



  const handleSignup = async () => {
    if (!email || !password || !agreeTerms) {
      setShowTermsModal(true);
      return;
    }
    const trimmedEmail = email.trim();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const emailDomain = trimmedEmail.split("@")[1]?.toLowerCase() || "";
    const knownDomains = [
      "gmail.com",
      "outlook.com",
      "hotmail.com",
      "yahoo.com",
      "icloud.com",
      "naver.com",
      "daum.net",
      "hanmail.net",
      "kakao.com",
    ];
    const gmailTypoDomains = ["ggmail.com", "gmial.com", "gamil.com", "gmai.com", "gmail.con"];
    const levenshtein = (a, b) => {
      const dp = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
      for (let i = 0; i <= a.length; i += 1) dp[i][0] = i;
      for (let j = 0; j <= b.length; j += 1) dp[0][j] = j;
      for (let i = 1; i <= a.length; i += 1) {
        for (let j = 1; j <= b.length; j += 1) {
          const cost = a[i - 1] === b[j - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,
            dp[i][j - 1] + 1,
            dp[i - 1][j - 1] + cost
          );
        }
      }
      return dp[a.length][b.length];
    };
    if (!emailValid) {
      openInfoModal("이메일 형식이 올바르지 않습니다.\n예: name@example.com", "오류");
      return;
    }
    if (gmailTypoDomains.includes(emailDomain)) {
      openInfoModal("이메일 도메인이 잘못되었습니다.\n예: gmail.com", "오류");
      return;
    }
    if (emailDomain) {
      let bestMatch = "";
      let bestScore = Infinity;
      for (const domain of knownDomains) {
        const score = levenshtein(emailDomain, domain);
        if (score < bestScore) {
          bestScore = score;
          bestMatch = domain;
        }
      }
      if (bestMatch && bestScore > 0 && bestScore <= 2) {
        openInfoModal(`이메일 도메인이 맞는지 확인해주세요.\n혹시 ${bestMatch} 아닌가요?`, "오류");
        return;
      }
    }
    const pwErrors = getPasswordErrors(password, email);
    if (pwErrors.length > 0) {
      openInfoModal("비밀번호 규칙 미달:\n\n• " + pwErrors.join("\n• ") + "\n\n다시 입력해주세요.");
      setPassword("");
      return;
    }
    
    // 회원가입 시작 - 즉시 로그인 상태 차단
    isSigningUp.current = true;
    setUser(null);
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
      // 즉시 로그아웃
      await signOut(auth);
      // 인증 이메일 발송
      await sendEmailVerification(userCredential.user);
      setPendingEmailVerification(true);
      setVerificationEmail(trimmedEmail);
      setShowSignupModal(false);
      setShowEmailVerificationModal(true);
      setEmail("");
      setPassword("");
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setShowSignupModal(false);
        setShowDuplicateModal(true);
      } else {
        openInfoModal("회원가입 실패: " + error.message, "오류");
      }
    } finally {
      isSigningUp.current = false;
    }
  };

  const sendMessage = async ({ recipientUid, rentId, rentTitle, content }) => {
    if (!content.trim()) return;
    try {
      await addDoc(collection(db, "messages"), {
        senderUid: user.uid,
        recipientUid,
        rentId,
        rentTitle,
        content,
        createdAt: serverTimestamp(),
        isRead: false,
      });
    } catch (error) {
      openInfoModal("메시지 전송 실패: " + error.message, "오류");
    }
  };

  const handleReplySend = async (msg) => {
    if (!replyContent.trim()) return;
    try {
      const payload = {
        senderUid: user.uid,
        recipientUid: msg.senderUid,
        rentId: msg.rentId,
        rentTitle: msg.rentTitle || "제목 없음",
        content: replyContent,
        createdAt: serverTimestamp(),
        isRead: false
      };
      const docRef = await addDoc(collection(db, "messages"), payload);
      setMessages((prev) => [{ id: docRef.id, ...payload }, ...prev]);
      openInfoModal("답장이 전송되었습니다.");
      setReplyTarget(null);
    } catch (error) {
      openInfoModal("답장 전송 실패: " + error.message, "오류");
    }
  };

  const handleSendMessage = async (rent) => {
    if (!messageText.trim()) return;

    try {
      const payload = {
        senderUid: user.uid,
        recipientUid: rent.uid,
        rentId: rent.id,
        rentTitle: rent.title, // ✅ 여기가 핵심
        content: messageText,
        createdAt: serverTimestamp(),
        isRead: false
      };
      const docRef = await addDoc(collection(db, "messages"), payload);
      setMessages((prev) => [{ id: docRef.id, ...payload }, ...prev]);

      openInfoModal("메시지가 전송되었습니다.");
      setMessageText("");
      logActivity("message_send", { rentId: rent.id, rentTitle: rent.title || "" });
    } catch (error) {
      console.error("메시지 전송 실패:", error);
      openInfoModal("메시지 전송에 실패했습니다.", "오류");
    }
  };

  const handleDeleteMessage = async (id) => {
    const confirmed = await openConfirmModal("정말 이 메시지를 삭제하시겠습니까?");
    if (!confirmed) return;

    try {
      const targetMsg = inboxMessages.find((msg) => msg.id === id);
      await deleteDoc(doc(db, "messages", id));

      // ✅ 삭제된 메시지를 state에서 즉시 제거
      setInboxMessages((prev) => prev.filter((msg) => msg.id !== id));
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
      if (targetMsg && !targetMsg.isRead) {
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      }
    } catch (error) {
      openInfoModal("메시지 삭제 실패: " + error.message, "오류");
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("로그아웃 실패:", error);
    } finally {
      try {
        if (window.Kakao?.Auth) {
          window.Kakao.Auth.logout();
        }
      } catch (e) {
        logWarn("카카오 로그아웃 실패", e);
      }
      setUser(null);
      setIsAdmin(false);
      setShowAdminPage(false);
      setPendingLinkCredential(null);
      setPendingLinkEmail("");
      clearPendingKakaoLink();
      setShowForm(false);
      setShowInbox(false);
      setShowMyListings(false);
      localStorage.removeItem('kakao_signup_mode');
    }
  };

  const handleGoogleLogin = () => {
    logDebug("[handleGoogleLogin] 버튼 클릭됨");
    initiateGoogleAuth({ isSignup: false });
  };

  // 구글 리다이렉트 콜백 처리 (앱 시작 시 1회)
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (result) => {
        if (!result || !result.user) {
          if (blockAuthSync.current) {
            logWarn("[getRedirectResult] 결과 없음, blockAuthSync 해제");
            blockAuthSync.current = false;
          }
          return;
        }
        logDebug("[getRedirectResult] 리다이렉트 결과 감지, finalizeGoogleAuth 호출");
        const isSignupMode = localStorage.getItem('google_signup_mode') === 'true';
        await finalizeGoogleAuth(result, { isSignupModeOverride: isSignupMode });
      })
      .catch((error) => {
        if (error?.code !== 'auth/no-auth-event') {
          console.error("Google 리다이렉트 콜백 에러:", error);
          console.error("에러 코드:", error?.code);
          console.error("에러 메시지:", error?.message);

          if (error?.code === "auth/account-exists-with-different-credential") {
            handleAccountLinkPrompt(error)
              .then((handled) => {
                if (handled) return;
                setAuthError("Google 로그인 실패: " + (error?.message || "알 수 없는 오류"));
                blockAuthSync.current = false;
                localStorage.removeItem('google_signup_mode');
                setIsLoggingIn(false);
              })
              .catch(() => {
                blockAuthSync.current = false;
                localStorage.removeItem('google_signup_mode');
                setIsLoggingIn(false);
              });
            return;
          }
          
          // 네트워크 오류인 경우 안내
          if (error?.code === 'auth/network-request-failed') {
            setAuthError("네트워크 연결을 확인해주세요. 인터넷이 정상인지 확인 후 다시 시도해주세요.");
          }
        }
        localStorage.removeItem('google_signup_mode');
        blockAuthSync.current = false;
      });
  }, [finalizeGoogleAuth, handleAccountLinkPrompt]);


  const fetchNotices = useCallback(async () => {
    try {
      setNoticesLoading(true);
      const q = query(
        collection(db, "notices"),
        orderBy("createdAt", "desc"),
        limit(NOTICES_LIMIT)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs
        .map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        .filter((notice) => notice.isPublished !== false);
      setNotices(data);
    } catch (error) {
      console.error("공지사항 조회 실패:", error);
    } finally {
      setNoticesLoading(false);
    }
  }, []);



  const fetchMessages = useCallback(async ({ reset = false } = {}) => {
    if (!user) return;
    if (isMessagesLoadingRef.current) return;
    isMessagesLoadingRef.current = true;
    setIsMessagesLoading(true);
    try {
      if (reset) {
        setLastReceivedDoc(null);
        setLastSentDoc(null);
        lastReceivedDocRef.current = null;
        lastSentDocRef.current = null;
        setHasMoreInbox(true);
      }

      const receivedBase = [
        collection(db, "messages"),
        where("recipientUid", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(MESSAGES_PAGE_SIZE),
      ];
      const receivedQuery = (!reset && lastReceivedDocRef.current)
        ? query(...receivedBase, startAfter(lastReceivedDocRef.current))
        : query(...receivedBase);

      const sentBase = [
        collection(db, "messages"),
        where("senderUid", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(MESSAGES_PAGE_SIZE),
      ];
      const sentQuery = (!reset && lastSentDocRef.current)
        ? query(...sentBase, startAfter(lastSentDocRef.current))
        : query(...sentBase);

      const [receivedSnap, sentSnap] = await Promise.all([
        getDocs(receivedQuery),
        getDocs(sentQuery),
      ]);

      const received = receivedSnap.docs.map(docItem => ({ id: docItem.id, ...docItem.data() }));
      const sent = sentSnap.docs.map(docItem => ({ id: docItem.id, ...docItem.data() }));

      setInboxMessages((prev) => {
        const mergedInbox = mergeMessagesById(reset ? [] : prev, received).sort(sortMessagesDesc);
        setUnreadCount(mergedInbox.filter(m => !m.isRead).length);
        return mergedInbox;
      });
      setMessages((prev) => (
        mergeMessagesById(reset ? [] : prev, [...received, ...sent]).sort(sortMessagesDesc)
      ));

      const nextLastReceived = receivedSnap.docs[receivedSnap.docs.length - 1] || lastReceivedDocRef.current;
      const nextLastSent = sentSnap.docs[sentSnap.docs.length - 1] || lastSentDocRef.current;
      setLastReceivedDoc(nextLastReceived);
      setLastSentDoc(nextLastSent);
      lastReceivedDocRef.current = nextLastReceived;
      lastSentDocRef.current = nextLastSent;
      setHasMoreInbox(receivedSnap.docs.length === MESSAGES_PAGE_SIZE);
    } catch (error) {
      console.error("메시지 불러오기 실패:", error);
      if (error.code === 'permission-denied') {
        reportPermissionDenied("fetchMessages", error);
        setMessages([]);
        setInboxMessages([]);
        setUnreadCount(0);
      }
    } finally {
      isMessagesLoadingRef.current = false;
      setIsMessagesLoading(false);
    }
  }, [user, MESSAGES_PAGE_SIZE, sortMessagesDesc]);







  useEffect(() => {
    if (!user) return;
    fetchMessages({ reset: true });
  }, [user, fetchMessages]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (blockAuthSync.current) {
        // 리다이렉트 인증 처리 중에는 상태를 건드리지 않고 대기
        console.debug("[onAuthStateChanged] blockAuthSync=true, 상태 업데이트 스킵 (사용자:", currentUser?.email || "none", ")");
        return;
      }
      // 회원가입 프로세스 중이면 무시
      if (isSigningUp.current) {
        logDebug("회원가입 진행 중 - onAuthStateChanged 무시");
        setIsAdmin(false);
        setShowAdminPage(false);
        return;
      }
      
      if (currentUser) {
        setAgreeTerms(true);
        // 이메일 회원가입 사용자가 인증되지 않았으면 즉시 로그아웃 + 상태 해제
        if (currentUser.providerData?.length > 0 && currentUser.providerData[0]?.providerId === 'password') {
          if (!currentUser.emailVerified) {
            logDebug("이메일 미인증 사용자 감지 → 강제 로그아웃");
            try { await signOut(auth); } catch (_) {}
            setUser(null);
            setPendingEmailVerification(true);
            return;
          }
          setPendingEmailVerification(false);
          // 인증 완료된 사용자: Firestore에 저장
          try {
            const userRef = doc(db, "users", currentUser.uid);
            const userDoc = await getDoc(userRef);
            // 아직 Firestore에 저장되지 않았으면 저장
            if (!userDoc.exists()) {
              await setDoc(userRef, {
                uid: currentUser.uid,
                provider: "email",
                email: currentUser.email,
                displayName: currentUser.displayName || "",
                photoURL: currentUser.photoURL || null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
              logDebug("이메일 인증 완료 후 Firestore에 사용자 저장");
            }
          } catch (e) {
            logWarn("사용자 프로필 저장 실패", e);
          }
        }

        // 사용자 정지 상태 확인
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userRef);
          const status = userDoc.data()?.status;
          if (status === "정지") {
            openInfoModal("관리자에 의해 계정이 정지되었습니다. 문의가 필요하면 관리자에게 연락해주세요.");
            try { await signOut(auth); } catch (_) {}
            setUser(null);
            setIsAdmin(false);
            setShowAdminPage(false);
            return;
          }
        } catch (e) {
          logWarn("사용자 상태 확인 실패", e);
        }
      }
      setUser(currentUser);
      const adminFlag = isAdminUser(currentUser);
      logDebug("[DEBUG] onAuthStateChanged: adminFlag =", adminFlag, "user:", currentUser?.email);
      setIsAdmin(adminFlag);
      setShowAdminPage(adminFlag);
    });
    return () => unsubscribe();
  }, [openInfoModal]);

  // 이메일 인증 모달이 열려 있는 동안은 강제로 로그인 상태를 해제해 UI 노출을 막는다.
  useEffect(() => {
    if (showEmailVerificationModal) {
      setUser(null);
      setPendingEmailVerification(true);
      setIsAdmin(false);
      setShowAdminPage(false);
      // 세션이 남아있지 않도록 한번 더 로그아웃 시도 (이미 로그아웃 상태여도 에러 무시)
      signOut(auth).catch(() => {});
    }
  }, [showEmailVerificationModal]);

  useEffect(() => {
    // URL에서 카카오 인증 code 추출 및 처리
    const tryHandleKakaoCode = async () => {
      if (kakaoResumeAttempted.current) return;
      if (user) return;
      if (typeof window === "undefined") return;
      
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const stateParam = params.get("state");
      
      if (!code) {
        // code 없으면 저장된 토큰 확인
        try {
          await ensureKakaoSDK();
          const token = window?.Kakao?.Auth?.getAccessToken?.();
          if (!token) return;
          kakaoResumeAttempted.current = true;
          logDebug("카카오 리다이렉트 복구 시도: 저장된 액세스 토큰 발견");
          await handleKakaoLoginCallback({ resumed: true });
        } catch (e) {
          kakaoResumeAttempted.current = true;
          logWarn("카카오 리다이렉트 복구 실패", e);
          try { window?.Kakao?.Auth?.logout(); } catch (_) {}
        }
        return;
      }

      // code가 있으면 서버에서 처리
      kakaoResumeAttempted.current = true;
      const localFlag = localStorage.getItem('kakao_signup_mode');
      logDebug("[Code Flow] stateParam:", stateParam, "localFlag:", localFlag);
      // signup으로 명시적으로 지정된 경우만 회원가입 모드
      const isSignup = (stateParam === 'signup' && localFlag === 'true');
      logDebug("[Code Flow] isSignup:", isSignup);
      
      setIsLoggingIn(true);
      try {
        logDebug("카카오 code 감지:", code.substring(0, 10) + "...");
        
        // code를 서버(Firebase Function)에 전달해서 액세스 토큰 교환 + Firebase 커스텀 토큰 획득
        const res = await fetch(KAKAO_CUSTOM_TOKEN_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, redirectUri: getKakaoRedirectUri(), isSignup }),
        });

        if (!res.ok) {
          const raw = await res.text();
          let message = raw;
          try {
            const parsed = JSON.parse(raw);
            message = parsed?.message || parsed?.error || raw;
          } catch (_) {}

          if (res.status === 403 && `${message}`.toLowerCase().includes("user not found")) {
            openInfoModal("계정을 찾을 수 없습니다. 회원가입 후 이용해주세요.", "안내");
          } else {
            openInfoModal("카카오 로그인에 실패했습니다. 잠시 후 다시 시도해주세요.", "오류");
          }
          window.history.replaceState({}, document.title, window.location.pathname);
          localStorage.removeItem('kakao_signup_mode');
          return;
        }

        const responseData = await res.json();
        const { customToken, isExistingUser } = responseData;
        logDebug("[Kakao] 서버 응답 전체:", responseData);
        logDebug("[Kakao] customToken 존재:", !!customToken);
        logDebug("[Kakao] customToken 길이:", customToken?.length);
        logDebug("[Kakao] customToken 형태:", typeof customToken);
        logDebug("[Kakao] isExistingUser:", isExistingUser);
        
        // 기존 사용자이고 회원가입 모드면 로그인하지 않고 바로 안내
        if (isExistingUser && isSignup) {
          logDebug("[Code Flow] 기존 사용자 감지 (함수에서): 회원가입 모드 차단");
          setShowDuplicateModal(true);
          window.history.replaceState({}, document.title, window.location.pathname);
          localStorage.removeItem('kakao_signup_mode');
          return;
        }
        
        if (!customToken) {
          throw new Error("서버에서 커스텀 토큰을 반환하지 않았습니다.");
        }

        // Firebase 로그인
        logDebug("[Kakao] Firebase signInWithCustomToken 시작... (토큰: " + customToken.substring(0, 20) + "...)");
        const userCredential = await signInWithCustomToken(auth, customToken);
        logDebug("[Kakao] Firebase signInWithCustomToken 성공:", userCredential.user.uid);
        
        // 회원가입과 로그인 모두 사용자 상태 설정
        setUser(userCredential.user);
        setIsAdmin(false);
        setShowAdminPage(false);
        setPendingEmailVerification(false);
        setShowEmailVerificationModal(false);

        setShowLoginModal(false);
        setShowSignupModal(false);
        setAuthError("");
        
        // localStorage signup 플래그 제거
        localStorage.removeItem('kakao_signup_mode');
        
        // URL에서 code 제거
        window.history.replaceState({}, document.title, window.location.pathname);

        // 회원가입 모드면 이메일 모달 호출
        if (isSignup) {
          logDebug("[Kakao] 회원가입 모드: 이메일 모달 호출 예약...");
          setTimeout(() => {
            ensureSocialEmail(userCredential.user, "kakao", null).catch(() => {});
          }, 300);
        }
      } catch (e) {
        console.error("카카오 code 처리 실패:", e);
        console.error("에러 코드:", e?.code);
        console.error("에러 메시지:", e?.message);
        console.error("전체 에러 객체:", e);
        console.error("스택 트레이스:", e?.stack);
        
        if (e?.code === 'auth/network-request-failed' || e?.message?.includes('network')) {
          setAuthError("네트워크 오류: 인터넷 연결을 확인해주세요. 잠시 후 다시 시도해주세요.");
        } else if (e?.code === 'auth/invalid-custom-token') {
          setAuthError("인증 토큰 오류: 서버 설정을 확인해주세요.");
        } else {
          setAuthError("로그인 실패: " + (e?.message || "알 수 없는 오류"));
        }
        try { window?.Kakao?.Auth?.logout(); } catch (_) {}
      }
      finally {
        setIsLoggingIn(false);
      }
    }; // end tryHandleKakaoCode

    // 실행
    tryHandleKakaoCode();
  }, [
    user,
    KAKAO_CUSTOM_TOKEN_ENDPOINT,
    ensureKakaoSDK,
    handleKakaoLoginCallback,
    ensureSocialEmail,
    getKakaoRedirectUri,
    openInfoModal,
  ]);

  useEffect(() => {
    // 마지막 사용 이메일 불러오기
    try {
      const savedEmail = localStorage.getItem('krent_last_email');
      if (savedEmail) setLastEmail(savedEmail);
    } catch (_) {
      // localStorage 접근 불가한 환경 대비
    }
  }, []);

  useEffect(() => {
    try {
      const savedAgree = localStorage.getItem('krent_agree_terms');
      if (savedAgree === 'true') setAgreeTerms(true);
    } catch (_) {
      // localStorage 접근 불가한 환경 대비
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('krent_agree_terms', agreeTerms ? 'true' : 'false');
    } catch (_) {
      // localStorage 접근 불가한 환경 대비
    }
  }, [agreeTerms]);

  useEffect(() => {
    localStorage.setItem('showMyListings', showMyListings);
  }, [showMyListings]);

  useEffect(() => {
    if (!user && showMyListings) {
      // 로그인 전에는 전체 보기로 기본화
      setShowMyListings(false);
    }
  }, [user, showMyListings]);

  useEffect(() => {
    if (!user && showForm) {
      setShowForm(false);
    }
  }, [user, showForm]);

  // 초기 로드만 수행 (의존성 배열 비워두기)
  useEffect(() => {
    if (initialRentsLoadedRef.current) return;
    initialRentsLoadedRef.current = true;
    fetchRents({ reset: true });
  }, [fetchRents]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  useEffect(() => {
    if (!user || isAdmin || notices.length === 0) return;
    const latest = notices[0];
    if (!latest?.id) return;
    const key = `notice_seen_${user.uid}_${latest.id}`;
    if (!localStorage.getItem(key)) {
      setNoticePopup(latest);
      setShowNoticePopup(true);
    }
  }, [user, isAdmin, notices]);

  const passwordErrors = getPasswordErrors(password, email);




  useEffect(() => {
    if (!showInbox || unreadCount === 0) return;
    const markAsRead = async () => {
      const unread = inboxMessages.filter(m => !m.isRead);
      for (const msg of unread) {
        await updateDoc(doc(db, "messages", msg.id), { isRead: true });
      }
      setInboxMessages((prev) => prev.map((msg) => (
        msg.isRead ? msg : { ...msg, isRead: true }
      )));
      setUnreadCount(0);
    };
    markAsRead();
  }, [showInbox, inboxMessages, unreadCount]);

  if (isAdmin && showAdminPage) {
    return (
      <AdminPage
        user={user}
        onLogout={handleLogout}
        onExit={() => setShowAdminPage(false)}
      />
    );
  }
  return (
    <>
      {isLoggingIn && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[70]">
          <div className="bg-white px-5 py-3 rounded-lg shadow-lg text-sm font-semibold text-gray-800">
            로그인 중...
          </div>
        </div>
      )}
      <div className="rentlist max-w-6xl mx-auto p-4 leading-[1.4]">
        {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="section-title text-2xl font-bold">알림</h2>
              <button
                onClick={handleCloseDuplicateModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <p className="desc-text text-gray-800 mb-4">이미 가입된 계정입니다. 로그인해 주세요.</p>
            <button
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-semibold transition-colors"
              onClick={handleCloseDuplicateAndOpenLogin}
            >
              확인
            </button>
          </div>
        </div>
      )}

        {showLoginHelpModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="section-title text-2xl font-bold">안내</h2>
                <button
                  onClick={() => setShowLoginHelpModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="desc-text text-gray-800 mb-4 whitespace-pre-line">{loginHelpMessage}</p>
              <div className="flex">
                <button
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg font-semibold transition-colors"
                  onClick={() => setShowLoginHelpModal(false)}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}

        {showInfoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="section-title text-2xl font-bold">{infoModalTitle}</h2>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="desc-text text-gray-800 mb-4 whitespace-pre-line">{infoModalMessage}</p>
              <div className="flex">
                <button
                  className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg font-semibold transition-colors"
                  onClick={() => setShowInfoModal(false)}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90]">
            <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="section-title text-2xl font-bold">{confirmModalTitle}</h2>
                <button
                  onClick={() => resolveConfirmModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="desc-text text-gray-800 mb-4 whitespace-pre-line">{confirmModalMessage}</p>
              <div className="flex gap-2">
                <button
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg font-semibold transition-colors"
                  onClick={() => resolveConfirmModal(false)}
                >
                  취소
                </button>
                <button
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-semibold transition-colors"
                  onClick={() => resolveConfirmModal(true)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}

        {showNoticePopup && noticePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-[420px] max-w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="section-title text-2xl font-bold">공지사항</h2>
                <button
                  onClick={closeNoticePopup}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="mb-3">
                <div className="text-lg font-semibold text-gray-900">
                  {noticePopup.title || "(제목 없음)"}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(noticePopup.createdAt)}
                </div>
              </div>
              <p className="desc-text text-gray-800 whitespace-pre-line mb-5">
                {noticePopup.content || ""}
              </p>
              <button
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-semibold transition-colors"
                onClick={closeNoticePopup}
              >
                확인
              </button>
            </div>
          </div>
        )}

        {showEmailCollectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-[420px] max-w-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="section-title text-2xl font-bold">이메일 입력</h2>
                <button
                  onClick={handleCancelEmailCollect}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="desc-text text-gray-700 mb-3">
                안내 및 공지 수신을 위해 이메일을 입력해주세요.
              </p>
              <input
                type="email"
                value={emailCollectValue}
                onChange={(e) => {
                  setEmailCollectValue(e.target.value);
                  if (emailCollectError) setEmailCollectError("");
                }}
                placeholder="이메일 입력"
                className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {emailCollectError && (
                <div className="mt-2 text-xs text-red-600">{emailCollectError}</div>
              )}
              <button
                className="w-full mt-4 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-semibold transition-colors"
                onClick={handleSaveCollectedEmail}
              >
                저장
              </button>
            </div>
          </div>
        )}

          {showLoginModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-2xl shadow-xl w-96 max-w-full border border-slate-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="section-title text-2xl font-bold">로그인</h2>
                  </div>
                  <button
                    onClick={() => setShowLoginModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
                
                <input
                  type="email"
                  placeholder="이메일"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  className="w-full border border-slate-200 h-11 px-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                />
                {lastEmail && !email && (
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                    <span>최근 사용한 이메일: {lastEmail}</span>
                    <button
                      className="text-blue-600 hover:underline font-semibold"
                      onClick={() => handleEmailChange(lastEmail)}
                    >
                      자동 입력
                    </button>
                  </div>
                )}
                <input
                  type="password"
                  placeholder="비밀번호"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-slate-200 h-11 px-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex justify-end mt-2">
                  <button
                    className="text-xs text-blue-600 hover:underline"
                    onClick={handlePasswordReset}
                  >
                    비밀번호 재설정
                  </button>
                </div>
                <button
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 rounded-xl font-semibold transition-colors mt-3"
                  onClick={handleLogin}
                >
                  로그인
                </button>

                <div className="flex items-center my-4">
                  <div className="flex-1 border-t border-slate-200" />
                  <span className="px-3 text-xs text-slate-500">또는</span>
                  <div className="flex-1 border-t border-slate-200" />
                </div>

                <button
                  className="w-full bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 h-11 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 mb-2"
                  onClick={handleGoogleLogin}
                >
                  <span className="text-red-500">🔍</span> Google로 로그인
                </button>
                
                <button
                  className="w-full bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 h-11 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                  onClick={handleKakaoLogin}
                >
                  <span className="text-yellow-500">💬</span> 카카오로 로그인
                </button>

                <div className="mt-4 text-center text-xs text-slate-600">
                  처음이신가요?
                  <button
                    className="ml-1 text-blue-600 hover:underline font-semibold"
                    onClick={() => { setShowLoginModal(false); setShowSignupModal(true); }}
                  >
                    회원가입
                  </button>
                </div>

                {authError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    {authError}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 이메일 인증 대기 모달 */}
          {showEmailVerificationModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-w-full">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="section-title text-2xl font-bold">이메일 인증</h2>
                  <button
                    onClick={() => setShowEmailVerificationModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="desc-text text-gray-700 mb-2">
                    <strong>{verificationEmail}</strong>로 인증 이메일을 발송했습니다.
                  </p>
                  <p className="desc-text text-gray-600 mb-3">
                    이메일함을 확인하고 인증 링크를 클릭해주세요.
                  </p>
                  <p className="desc-text text-gray-500">
                    ※ 스팸 메일함도 함께 확인해주세요.
                  </p>
                </div>

                <button
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors"
                  onClick={() => setShowEmailVerificationModal(false)}
                >
                  닫기
                </button>
              </div>
            </div>
          )}

          <div className="max-w-6xl mx-auto px-3 py-4 sm:px-4">
            <div className="mb-6">
              <div className="bg-gradient-to-r from-[#1A365D] via-[#1D4A7A] to-[#2563A8] rounded-xl sm:rounded-2xl shadow-lg py-1.5 sm:py-2 md:py-3 px-3 sm:px-4 md:px-6 text-white mb-4">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <img
                    src="/logo-new2.png?v=1"
                    alt="Logo"
                    className="w-14 h-14 sm:w-[80px] sm:h-[80px] md:w-[100px] md:h-[100px] object-contain cursor-pointer"
                    onClick={resetToHome}
                  />
                  <div className="text-center flex-1 min-w-0">
                    <p className="text-white text-xs sm:text-sm md:text-lg font-bold tracking-widest mb-1 sm:mb-2">KRENT.CA</p>
                    <h1 className="text-xl sm:text-3xl md:text-4xl font-bold cursor-pointer hover:opacity-90 transition-opacity" onClick={resetToHome}>
                      캐나다 한인 렌트</h1>
                  </div>
                  <div className="w-auto flex items-center justify-end">
                    {user && !pendingEmailVerification ? (
                      <>
                        <button
                          className="sm:hidden bg-white text-blue-700 px-2.5 py-1.5 rounded-lg transition-colors font-semibold text-[11px] shadow-sm whitespace-nowrap"
                          onClick={handleLogout}
                        >
                          로그아웃
                        </button>
                        <div className="hidden sm:flex flex-wrap sm:flex-nowrap items-center justify-end gap-1.5 sm:gap-2">
                        <button
                          className="bg-white/10 border border-white/30 hover:bg-white/20 text-white px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors font-medium text-[11px] sm:text-xs shadow-sm whitespace-nowrap"
                          onClick={() => setShowMyListings(prev => !prev)}
                        >
                          {showMyListings ? "전체 렌트 보기" : "내 등록렌트"}
                        </button>

                        <button
                          className="relative bg-white/10 border border-white/30 hover:bg-white/20 text-white px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors font-medium text-[11px] sm:text-xs shadow-sm whitespace-nowrap"
                          onClick={() => setShowInbox(true)}
                        >
                          받은 메시지함
                          {unreadCount > 0 && (
                            <span className="absolute -top-2 -right-2 inline-block bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full min-w-[18px] text-center">
                              {unreadCount}
                            </span>
                          )}
                        </button>

                        <button className="bg-white text-blue-700 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors font-semibold text-[11px] sm:text-xs shadow-sm whitespace-nowrap" onClick={handleLogout}>로그아웃</button>
                      </div>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          className="h-7 sm:h-8 px-2.5 sm:px-3 rounded-full text-white bg-blue-400 hover:bg-blue-300 text-[11px] sm:text-xs font-bold transition inline-flex items-center gap-1.5 whitespace-nowrap shadow-md"
                          onClick={() => setShowLoginModal(true)}
                        >
                          <span className="text-[11px] sm:text-xs">👤</span>
                          로그인
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {user && !pendingEmailVerification && (
              <div className="sm:hidden mb-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    className="bg-slate-100 border border-slate-200 text-slate-700 px-2.5 py-2 rounded-lg text-xs font-semibold"
                    onClick={() => setShowMyListings(prev => !prev)}
                  >
                    {showMyListings ? "전체 렌트 보기" : "내 등록렌트"}
                  </button>
                  <button
                    className="relative bg-slate-100 border border-slate-200 text-slate-700 px-2.5 py-2 rounded-lg text-xs font-semibold"
                    onClick={() => setShowInbox(true)}
                  >
                    받은 메시지함
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 inline-block bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[16px] text-center">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 mb-4 sm:mb-5 -mx-3 sm:mx-0 px-3 sm:px-0">
              {categoryButtons.map(cat => (
                <button
                  key={cat}
                  className={`h-11 sm:h-12 rounded-xl text-sm sm:text-base font-semibold transition-all duration-150 border shadow-sm flex items-center justify-center gap-2 ${selectedCategory === cat
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setFilters(prev => ({ ...prev, category: cat }));
                  }}
                >
                  <span>{categoryLabels[cat] || cat}</span>
                </button>
              ))}
            </div>
            
            <div className="w-full mb-5">
              <div className="bg-white border border-slate-200 shadow rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5">
                <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 items-center">
                  <select 
                    className="border border-gray-200 rounded-lg h-11 px-3 text-xs sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 flex-1 sm:w-[180px] min-w-[140px]" 
                    value={area} 
                    onChange={e => {
                      setArea(e.target.value);
                      setCity(""); // 지역 변경시 도시 초기화
                    }}
                  >
                    <option value="">-- 지역 선택 --</option>
                    {Object.keys(citiesByArea).map((region) => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>

                  <select 
                    className="border border-gray-200 rounded-lg h-11 px-3 text-xs sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 flex-1 sm:w-[180px] min-w-[140px] disabled:opacity-60" 
                    value={city} 
                    onChange={e => setCity(e.target.value)}
                    disabled={!area}
                  >
                    <option value="">-- 도시 선택 --</option>
                    {area && citiesByArea[area]?.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>

                  <select className="border border-gray-200 rounded-lg h-11 px-3 text-xs sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 flex-1 sm:w-[140px] min-w-[130px]" value={price} onChange={e => setPrice(e.target.value)}>
                    <option value="">-- 가격 선택 --</option>
                    <option value="0-1000">$1000 이하</option>
                    <option value="1000-2000">$1000~$2000</option>
                    <option value="2000-3000">$2000~$3000</option>
                    <option value="3000-4000">$3000~$4000</option>
                    <option value="4000+">$4000 이상</option>
                  </select>

                  <input
                    className="border border-gray-200 rounded-lg h-11 px-3 text-xs sm:text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-full sm:w-[240px] md:w-[320px]"
                    placeholder="검색어"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    maxLength={20}
                  />

                  <div className="flex items-center justify-between gap-2 w-full sm:w-auto">
                    <button
                      className="bg-white text-slate-700 h-11 px-3 sm:px-4 rounded-lg border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs sm:text-sm"
                      onClick={() => {
                        if (!selectedCategory) {
                          openInfoModal("렌트 타입을 먼저 선택해주세요.");
                          return;
                        }
                        setShowDetailFilters((v) => !v);
                      }}
                    >
                      {showDetailFilters ? "상세조건 닫기" : "상세조건"}
                    </button>
                    <button className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-4 sm:px-6 rounded-lg shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300 text-xs sm:text-sm" onClick={handleSearch}>
                      🔍 매물 검색
                    </button>
                  </div>
                </div>

                {showDetailFilters && (
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
                    
                    <div className="space-y-3">
                      <div className="font-xs font-medium text-gray-600 uppercase tracking-wide">기본 정보</div>
                      <div className="flex flex-wrap gap-2 md:gap-3">
                        {!['콘도(전체렌트)', '하우스(전체렌트)'].includes(selectedCategory) && (
                          <select className="border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-[140px] bg-white" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
                            <option value="">성별(무관)</option>
                            <option value="남">남</option>
                            <option value="여">여</option>
                          </select>
                        )}
                        <>
                          <select
                            className="border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[130px] bg-white"
                            value={bedrooms}
                            onChange={(e) => setBedrooms(e.target.value)}
                          >
                            <option value="">침실 개수</option>
                            <option value="0">0 Room (Studio)</option>
                            <option value="1">1 Room</option>
                            <option value="1.5">1 Room + Den</option>
                            <option value="2">2 Room</option>
                            <option value="2.5">2 Room + Den</option>
                            <option value="3">3+ Room</option>
                          </select>

                          <select
                            className="border border-gray-300 rounded-lg p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[130px] bg-white"
                            value={bathrooms}
                            onChange={(e) => setBathrooms(e.target.value)}
                          >
                            <option value="">욕실 개수</option>
                            <option value="1">1 Bath</option>
                            <option value="2">2 Bath</option>
                            <option value="3">3+ Bath</option>
                          </select>
                        </>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="font-xs font-medium text-gray-600 uppercase tracking-wide">편의시설</div>
                      <div className="flex flex-wrap gap-2.5">
                        <label className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm cursor-pointer hover:bg-blue-50 transition">
                          <input
                            type="checkbox"
                            checked={parkingRequired}
                            onChange={(e) => setParkingRequired(e.target.checked)}
                            className="accent-blue-600 cursor-pointer"
                          />
                          주차 가능
                        </label>
                        <label className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm cursor-pointer hover:bg-blue-50 transition">
                          <input
                            type="checkbox"
                            checked={furnishedOnly}
                            onChange={(e) => setFurnishedOnly(e.target.checked)}
                            className="accent-blue-600 cursor-pointer"
                          />
                          가구 포함
                        </label>

                        <label className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm cursor-pointer hover:bg-blue-50 transition">
                          <input type="checkbox" checked={utilitiesIncluded} onChange={(e) => setUtilitiesIncluded(e.target.checked)} className="accent-blue-600 cursor-pointer" />
                          유틸리티 포함(전부/일부)
                        </label>

                        <label className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm cursor-pointer hover:bg-blue-50 transition">
                          <input type="checkbox" checked={internetIncluded} onChange={(e) => setInternetIncluded(e.target.checked)} className="accent-blue-600 cursor-pointer" />
                          인터넷 포함
                        </label>

                        <label className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm cursor-pointer hover:bg-blue-50 transition">
                          <input type="checkbox" checked={petFriendly} onChange={(e) => setPetFriendly(e.target.checked)} className="accent-blue-600 cursor-pointer" />
                          반려동물 허용
                        </label>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-gray-300">
                      <button
                        className="bg-white text-slate-700 h-10 px-4 rounded-lg border border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 text-xs sm:text-sm font-medium transition"
                        onClick={() => {
                          setArea("");
                          setCity("");
                          setPrice("");
                          setSearch("");
                          setBedrooms("");
                          setBathrooms("");
                          setParkingRequired(false);
                          setFurnishedOnly(false);
                          setGenderFilter("");
                          setUtilitiesIncluded(false);
                          setInternetIncluded(false);
                          setPetFriendly(false);
                        }}
                      >
                        조건초기화
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!user && (
            <div className="mb-3 mt-1 desc-text text-slate-500 flex items-start gap-2">
              <span className="text-base leading-none">ℹ️</span>
              <p className="leading-relaxed">렌트 매물 등록 및 메시지 전송은 로그인 후 가능합니다.</p>
            </div>
          )}

          {isSubmitting && <p className="text-center text-gray-600">등록 중입니다...</p>}
          {submitSuccess && <p className="text-center text-green-600">등록이 완료되었습니다.</p>}

          <div className="mb-4">

            {user ? (
              <button onClick={() => setShowForm(!showForm)} className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-md hover:shadow-lg transition">
                {showForm ? "매물 등록 닫기" : "매물 등록하기"}
              </button>
            ) : null}

          </div>

          {showForm && (
            <div className="bg-white/90 backdrop-blur border border-gray-100 shadow-xl rounded-2xl p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="section-title text-xl md:text-2xl font-bold text-gray-800">렌트 매물 등록</h2>
                <p className="desc-text text-gray-600"><span className="text-red-500">*</span> 표시는 필수 입력 항목입니다</p>
              </div>
              
              <div className="space-y-3">
                <div>
                  <select className="border border-gray-200 rounded-lg p-3 w-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400" name="category" value={form.category} onChange={handleChange}>
                    <option value="">-- 렌트 타입 선택 * --</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <input className="border border-gray-200 rounded-lg p-3 w-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="제목 *" name="title" value={form.title} onChange={handleChange} maxLength={80} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <select className="border border-gray-200 rounded-lg p-3 w-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400" name="area" value={form.area} onChange={(e) => {
                    handleChange(e);
                    setForm(prev => ({ ...prev, city: "" }));
                  }}>
                    <option value="">-- 지역 선택 * --</option>
                    {Object.keys(citiesByArea).map((region) => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                  <select 
                    className="border border-gray-200 rounded-lg p-3 w-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60" 
                    name="city" 
                    value={form.city} 
                    onChange={handleChange}
                    disabled={!form.area}
                  >
                    <option value="">-- 도시 선택 * --</option>
                    {form.area && citiesByArea[form.area]?.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {!isRentRequestCategory(form.category) && (
                  <div>
                    <input
                      className="border border-gray-200 rounded-lg p-3 w-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="우편번호 (예: M5H 2N2) *"
                      name="postalCode"
                      value={form.postalCode}
                      onChange={(e) => {
                        setForm(prev => ({ ...prev, postalCode: e.target.value }));
                        schedulePostalCodeGeocoding(e.target.value);
                      }}
                    />
                    {form.postalCodeCoords && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ 위치 인식됨: ({form.postalCodeCoords.lat.toFixed(4)}, {form.postalCodeCoords.lng.toFixed(4)})
                      </p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input className="border border-gray-200 rounded-lg p-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="가격 (예: $2,000) *" name="price" value={form.price} onChange={handleChange} />
                  
                  <label className="text-sm text-gray-700">
                    입주 날짜 <span className="text-red-500">*</span>
                    <input
                      className="border border-gray-200 rounded-lg p-3 w-full mt-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      type="date"
                      name="date"
                      value={form.date}
                      onChange={handleChange}
                      placeholder="입주 날짜"
                    />
                    <div className="mt-1">
                      <label className="text-sm">
                        <input
                          type="checkbox"
                          checked={form.date === "즉시가능"}
                          onChange={() => setForm(prev => ({ ...prev, date: prev.date === "즉시가능" ? "" : "즉시가능" }))}
                        /> 즉시 가능
                      </label>
                    </div>
                  </label>
                </div>

                {!isRentRequestCategory(form.category) && (
                  <div>
                    <label className="text-sm text-gray-700">
                      사진 (최대 8장, 장당 5MB 이하) <span className="text-red-500">*</span>
                      <input
                        className="border border-gray-200 rounded-lg p-3 w-full mt-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        type="file"
                        accept="image/*,.heic,.heif"
                        multiple
                        onChange={handleImageChange}
                      />
                    </label>
                    {form.images?.length > 0 && (
                      <div className="mt-2">
                        <div className="text-sm text-gray-600 mb-2">선택한 사진: {form.images.length} / 8</div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {form.images.map((img, idx) => (
                            <div
                              key={`upload-${idx}`}
                              className="relative border border-gray-200 rounded overflow-hidden"
                              draggable
                              onDragStart={() => handleDragStart(idx)}
                              onDragOver={handleDragOver}
                              onDrop={() => handleDrop(idx)}
                            >
                              <img src={img} alt={`upload-${idx}`} className="w-full h-32 object-cover" />
                              <div className="absolute top-1 right-1 flex flex-col gap-1">
                                <button
                                  type="button"
                                  className="bg-black bg-opacity-60 text-white text-xxs px-2 py-1 rounded disabled:opacity-40"
                                  onClick={() => handleMoveImage(idx, -1)}
                                  disabled={idx === 0}
                                >
                                  앞으로
                                </button>
                                <button
                                  type="button"
                                  className="bg-black bg-opacity-60 text-white text-xxs px-2 py-1 rounded disabled:opacity-40"
                                  onClick={() => handleMoveImage(idx, 1)}
                                  disabled={idx === form.images.length - 1}
                                >
                                  뒤로
                                </button>
                                <button
                                  type="button"
                                  className="bg-red-600 bg-opacity-80 text-white text-xxs px-2 py-1 rounded"
                                  onClick={() => handleRemoveImage(idx)}
                                >
                                  삭제
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 mb-2">
                <h3 className="section-title text-md font-semibold mb-2 text-gray-800">상세조건</h3>
                <div className="grid gap-2 grid-cols-1 md:grid-cols-2">
                  {!['콘도(전체렌트)', '하우스(전체렌트)'].includes(form.category) && (
                    <select className="border border-gray-200 rounded-lg p-3 w-full col-span-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400" name="gender" value={form.gender} onChange={handleChange}>
                      <option value="">성별(무관)</option>
                      <option value="남">남</option>
                      <option value="여">여</option>
                    </select>
                  )}

                  <>
                      <select
                        className="border border-gray-200 rounded-lg p-3 w-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        name="bedrooms"
                        value={form.bedrooms}
                        onChange={handleChange}
                      >
                        <option value="">침실 개수 *</option>
                        <option value="0">0 Room (Studio)</option>
                        <option value="1">1 Room</option>
                        <option value="1.5">1 Room + Den</option>
                        <option value="2">2 Room</option>
                        <option value="2.5">2 Room + Den</option>
                        <option value="3">3+ Room</option>
                      </select>

                      <select
                        className="border border-gray-200 rounded-lg p-3 w-full text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        name="bathrooms"
                        value={form.bathrooms}
                        onChange={handleChange}
                      >
                        <option value="">욕실 개수 *</option>
                        <option value="1">1 Bath</option>
                        <option value="2">2 Bath</option>
                        <option value="3">3+ Bath</option>
                      </select>
                    </>

                  <label className="text-sm text-gray-700 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                    <input
                      type="checkbox"
                      checked={!!form.parkingAvailable}
                      onChange={(e) => setForm(prev => ({ ...prev, parkingAvailable: e.target.checked }))}
                      className="accent-blue-600"
                    />
                    주차 가능
                  </label>

                  <label className="text-sm text-gray-700 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                    <input
                      type="checkbox"
                      checked={!!form.furnished}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, furnished: e.target.checked }))
                      }
                      className="accent-blue-600"
                    />
                    가구 포함(Furnished)
                  </label>

                  <label className="text-sm text-gray-700 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                    <input
                      type="checkbox"
                      checked={!!form.utilitiesIncluded}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, utilitiesIncluded: e.target.checked }))
                      }
                      className="accent-blue-600"
                    />
                    유틸리티 포함(전부/일부)
                  </label>

                  <label className="text-sm text-gray-700 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                    <input
                      type="checkbox"
                      checked={!!form.internetIncluded}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, internetIncluded: e.target.checked }))
                      }
                      className="accent-blue-600"
                    />
                    인터넷 포함
                  </label>

                  <label className="text-sm text-gray-700 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                    <input
                      type="checkbox"
                      checked={!!form.petFriendly}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, petFriendly: e.target.checked }))
                      }
                      className="accent-blue-600"
                    />
                    반려동물 허용
                  </label>
                </div>
              </div>

              <textarea
                className="border w-full mt-2 p-2"
                placeholder="상세 설명"
                name="description"
                value={form.description}
                onChange={handleChange}
                maxLength={1000}
                rows={6}
              />
              <button className="mt-4 w-full bg-blue-500 text-white py-2 rounded" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "등록 중입니다..." : "등록"}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:gap-5">
            {currentItems.map(rent => (
              <div key={rent.id} className="group flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="relative w-full sm:w-48 rounded-xl overflow-hidden">
                    <img
                      src={Array.isArray(rent.images) ? rent.images[0] : rent.image}
                      alt={rent.title}
                      loading="lazy"
                      className="w-full h-48 sm:h-28 object-cover rounded-xl ring-1 ring-slate-200 cursor-pointer"
                      onClick={() => {
                        setSelectedRent(rent);
                        const imgs = Array.isArray(rent.images) ? rent.images : rent.image ? [rent.image] : [];
                        openLightbox(imgs, 0);
                      }}
                    />
                    {rent.status === "렌트됨" && (
                      <span
                        className="absolute -left-10 top-4 flex w-36 -rotate-45 items-center justify-center bg-red-600 py-2 text-center text-sm font-extrabold leading-none tracking-[0.08em] text-white shadow-md sm:-left-9 sm:w-36 sm:py-1.5 sm:text-sm"
                      >
                        렌트완료
                      </span>
                    )}
                  </div>
                  <div className="flex-1 w-full">
                    <div>
                      <h2
                        className="text-base sm:text-lg font-semibold text-blue-600 cursor-pointer hover:text-blue-700 break-words"
                        onClick={() => setSelectedRent(rent)}
                      >
                        {rent.title}
                      </h2>
                      <div className="mt-1 text-base sm:text-lg font-bold text-slate-900">
                        {rent.price}
                      </div>
                      <div className="mt-1 text-sm text-slate-700">
                        침실 {formatBedroomsDisplay(rent.bedrooms)} · 화장실 {rent.bathrooms !== null && rent.bathrooms !== undefined && rent.bathrooms !== "" ? rent.bathrooms : "-"}
                      </div>
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      {rent.area} · {rent.city}
                    </div>
                    {rent.category && (
                      <div className="mt-1 text-sm text-slate-600">
                        {rent.category}
                      </div>
                    )}
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:text-sm text-slate-600">
                      <div>
                        <span className="text-slate-500">입주일</span> · {rent.date}
                      </div>
                      <div>
                        <span className="text-slate-500">업데이트</span> · {(() => {
                          const refreshedTs = toTimestamp(rent.lastRefreshedAt);
                          const updatedTs = toTimestamp(rent.updatedAt);
                          const createdTs = toTimestamp(rent.createdAt);
                          const bestTs = Math.max(refreshedTs, updatedTs, createdTs);
                          return bestTs ? new Date(bestTs).toLocaleDateString() : "";
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                {selectedRent?.id === rent.id && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">

                    {/*                     
                    <p><strong>도시:</strong> {selectedRent.location}</p>
                    <p><strong>가격:</strong> {selectedRent.price}</p>
                     */}
                    {shouldShowGender(selectedRent.category) && (
                      <p><strong>성별:</strong> {selectedRent.gender || "무관"}</p>
                    )}
                    <p><strong>정보:</strong> {summarizeConditions(selectedRent)}</p>
                    <p><strong>설명:</strong> {selectedRent.description}</p>
                    <p className="text-sm text-gray-600 mt-1"><strong>등록일:</strong> {formatDateOnly(selectedRent.createdAt)}</p>

                    <div className="mt-3">
                      {selectedRent.images?.map((img, idx) => (
                        <img
                          key={idx}
                          src={img}
                          alt="등록 이미지"
                          loading="lazy"
                          className="w-32 h-24 object-cover inline-block mr-2 rounded-lg ring-1 ring-slate-200 cursor-pointer"
                          onClick={() => openLightbox(selectedRent.images, idx)}
                        />
                      ))}
                    </div>

                    {selectedRent.postalCode && GOOGLE_MAPS_EMBED_KEY && (
                      <div className="mt-4">
                        <h3 className="font-semibold mb-2">위치 (대략적)</h3>
                        <iframe
                          width="100%"
                          height="300"
                          style={{ border: 0, borderRadius: '8px' }}
                          loading="lazy"
                          allowFullScreen
                          referrerPolicy="no-referrer-when-downgrade"
                          title={`위치 지도: ${selectedRent.postalCode || ''}`}
                          src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_EMBED_KEY}&q=${encodeURIComponent(selectedRent.postalCode + ', Canada')}&zoom=15`}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          ※ 개인정보 보호를 위해 대략적인 위치만 표시됩니다.
                        </p>
                      </div>
                    )}

                    {user?.uid !== rent.uid && (
                      <div className="mt-4">
                        <textarea
                          className="w-full border border-slate-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="이 매물에 대해 메시지를 보내세요"
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          disabled={!user}
                        />
                        <button
                          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-slate-300"
                          onClick={() => {
                            if (!user) {
                              openInfoModal("로그인 후 이용하세요");
                              return;
                            }
                            handleSendMessage(rent);
                          }}
                        >
                          메시지 보내기
                        </button>
                      </div>
                    )}
                    <button className="mt-4 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700" onClick={() => setSelectedRent(null)}>닫기</button>
                  </div>
                )}

                {user?.uid === rent.uid && showMyListings && (
                  <div className="flex gap-2 mt-2">
                    <button
                      className="text-sm text-blue-600 underline"
                      onClick={() => handleEdit(rent)}
                    >
                      수정
                    </button>

                    <button
                      className="text-sm text-red-600 underline"
                      onClick={() => handleDelete(rent.id)}
                    >
                      삭제
                    </button>

                    <button
                      className="text-sm text-green-600 underline"
                      onClick={() => handleRefresh(rent)}
                    >
                      위로올리기
                    </button>

                    {showMyListings && showInbox && messages.filter(m => m.rentId === rent.id && m.recipientUid === user?.uid).length > 0 && (
                      <div className="mt-2 bg-gray-50 border rounded p-2">
                        <h3 className="font-semibold mb-2">받은 메시지:</h3>
                        {messages
                          .filter(m => m.rentId === rent.id && m.recipientUid === user?.uid)
                          .map((msg, i) => (
                            <div key={i} className="border p-2 my-1 rounded text-sm">
                              <p>{msg.content}</p>
                              <p className="text-xs">{formatTimestamp(msg.createdAt)}</p>
                            </div>
                          ))}
                      </div>
                    )}
                    {rent.status !== "렌트됨" && (
                      <button
                        className="text-sm text-red-600 underline"
                        onClick={() => markAsRented(rent.id)}
                      >
                        렌트완료
                      </button>
                    )}

                  </div>
                )}

              </div>
            ))}
          </div>

          {hasMoreRents && (
            <div className="mt-5 flex justify-center">
              <button
                className="px-4 py-2 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
                onClick={() => fetchRents()}
                disabled={isRentsLoading}
              >
                {isRentsLoading ? "불러오는 중..." : "더 불러오기"}
              </button>
            </div>
          )}

          <div className="flex justify-center items-center mt-4 space-x-2 flex-wrap gap-1">
            {/* 처음 버튼 */}
            <button
              className="px-2 py-1 border rounded text-xs sm:text-sm hover:bg-gray-100 disabled:opacity-50"
              onClick={() => {
                setCurrentPage(1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === 1}
            >
              처음
            </button>
            
            {/* 이전 버튼 */}
            <button
              className="px-2 py-1 border rounded text-xs sm:text-sm hover:bg-gray-100 disabled:opacity-50"
              onClick={() => {
                setCurrentPage(currentPage - 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === 1}
            >
              ⬅
            </button>

            {/* 페이지 숫자 (현재페이지 중심 ±2) */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => Math.abs(page - currentPage) <= 2)
              .map(page => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded text-xs sm:text-sm ${
                    page === currentPage
                      ? 'bg-blue-500 text-white font-bold'
                      : 'hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {page}
                </button>
              ))}

            {/* 다음 버튼 */}
            <button
              className="px-2 py-1 border rounded text-xs sm:text-sm hover:bg-gray-100 disabled:opacity-50"
              onClick={() => {
                setCurrentPage(currentPage + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === totalPages}
              title={`현재: ${currentPage}/${totalPages}`}
            >
              ➡
            </button>
            
            {/* 마지막 버튼 */}
            <button
              className="px-2 py-1 border rounded text-xs sm:text-sm hover:bg-gray-100 disabled:opacity-50"
              onClick={() => {
                setCurrentPage(totalPages);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === totalPages}
            >
              마지막
            </button>
          </div>
          <div className="mt-4 px-4 pt-4 pb-1 text-sm text-gray-600 border-t">
            <p>
              렌트 유형을 정확히 선택하고
              <button
                type="button"
                onClick={() => setShowSafetyModal(true)}
                className="ml-1 font-semibold text-blue-600 hover:underline"
              >
                안전거래 수칙
              </button>
              을 확인하세요. 문의 및 허위매물 신고:
              <a href="mailto:admin@krent.ca" className="ml-1 font-semibold text-blue-600 hover:underline">admin@krent.ca</a>
            </p>
          </div>

          <div className="mt-0 text-sm text-gray-600 pl-1">
            <details>
              <summary className="cursor-pointer font-semibold text-blue-600 hover:underline">
                About Krent.ca
              </summary>
              <div className="mt-1 whitespace-pre-line leading-relaxed">
                Krent.ca는 캐나다 한인을 위한 렌트 정보 플랫폼입니다. Krent는 매물을 쉽고 편리하게 등록하고 찾을 수 있도록 체계적으로 구성되어 있습니다. 또한 대략적인 위치 정보 및 메시지 전송 기능을 제공하여 보다 편리하게 이용하실 수 있도록 하였습니다. 캐나다 내 모든 렌트 정보를 한곳에서 쉽고 빠르게 확인하실 수 있습니다.
              </div>
            </details>
          </div>

        {lightbox && (
          <div
            className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
            onClick={closeLightbox}
          >
            <div className="relative max-w-4xl w-full bg-white rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="bg-gray-900 p-3 flex justify-between items-center">
                <div className="text-white text-sm font-medium">
                  {lightbox.index + 1} / {lightbox.images.length}
                </div>
                <button
                  type="button"
                  className="text-white hover:text-gray-300 transition-colors text-xl font-bold"
                  onClick={closeLightbox}
                >
                  ✕
                </button>
              </div>

              <div className="bg-gray-100 flex items-center justify-center p-2" style={{ height: '60vh' }}>
                <img
                  src={lightbox.images[lightbox.index]}
                  alt="preview"
                  className="w-auto h-full object-contain"
                  style={{ maxWidth: '100%', minHeight: '350px' }}
                />
              </div>

              <button
                type="button"
                className="absolute top-1/2 left-3 -translate-y-1/2 bg-white hover:bg-gray-100 text-gray-800 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all"
                onClick={() => moveLightbox(-1)}
              >
                ◀
              </button>
              <button
                type="button"
                className="absolute top-1/2 right-3 -translate-y-1/2 bg-white hover:bg-gray-100 text-gray-800 w-10 h-10 rounded-full shadow-lg flex items-center justify-center transition-all"
                onClick={() => moveLightbox(1)}
              >
                ▶
              </button>
            </div>
          </div>
        )}



        {showSignupModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-96 max-w-full">
              <div className="flex justify-between items-center mb-4">
                  <h2 className="section-title text-2xl font-bold">회원가입</h2>
                <button
                  onClick={() => setShowSignupModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              
              <input
                type="email"
                placeholder="이메일"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className="w-full border border-gray-300 p-3 mb-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                placeholder="비밀번호"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 p-3 mb-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {password && (
                passwordErrors.length > 0 ? (
                  <div className="text-xs text-red-600 mb-3">
                    <p className="font-semibold">비밀번호 규칙을 만족해주세요:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {passwordErrors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-xs text-green-600 mb-3">안전한 비밀번호입니다.</div>
                )
              )}
              {!password && (
                <div className="text-xs text-gray-600 mb-3 space-y-1">
                  <p className="font-semibold">비밀번호 규칙</p>
                  <p>· 8자 이상 / 64자 이하</p>
                  <p>· 대문자·소문자·숫자·특수문자 중 3가지 이상 포함</p>
                  <p>· 공백/쉬운 패턴/이메일과 유사한 비밀번호 금지</p>
                </div>
              )}

              {/* 약관 동의 */}
              <label className="text-sm text-gray-600 mb-4 flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 cursor-pointer"
                  required
                />
                <span>
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-blue-500 hover:underline font-semibold"
                  >
                    이용약관
                  </button>
                  에 동의합니다.
                </span>
              </label>

              <button
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white py-3 rounded-lg font-semibold transition-colors"
                onClick={handleSignup}
                disabled={!email || !password || !agreeTerms || passwordErrors.length > 0}
              >
                가입하기
              </button>

              <div className="flex items-center my-5">
                <div className="flex-1 border-t border-gray-300" />
                <span className="px-3 text-sm text-gray-500">또는</span>
                <div className="flex-1 border-t border-gray-300" />
              </div>

              <div className="space-y-2">
                <button
                  className="w-full bg-white border-2 border-red-500 hover:bg-red-50 text-red-600 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  onClick={handleGoogleSignup}
                >
                  <span>🔍</span> Google로 가입
                </button>
                <button
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                  onClick={handleKakaoSignup}
                >
                  <span>💬</span> 카카오로 가입
                </button>
              </div>
            </div>
          </div>
        )}

        {showInbox && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-md max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-1">
                <h2 className="section-title text-xl font-semibold">받은 메시지</h2>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[20px]">
                    {unreadCount}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-4">최신 메시지부터 표시됩니다.</p>

              {inboxMessages.length === 0 ? (
                <p className="text-sm text-gray-600">메시지가 없습니다.</p>
              ) : (
                <>
                  {inboxMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`border rounded p-4 mb-3 ${
                        msg.isRead
                          ? "bg-white"
                          : "bg-blue-50 border-blue-200"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {msg.rentTitle || "제목 없음"}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            매물 ID: {msg.rentId}
                          </div>
                        </div>
                        <div className="text-xs text-slate-400 whitespace-nowrap">
                          {new Date(msg.createdAt?.seconds * 1000).toLocaleString()}
                        </div>
                      </div>

                      <div className="text-sm text-slate-800 mt-2 whitespace-pre-wrap">
                        {msg.content}
                      </div>

                      <div className="mt-2 flex gap-3">
                        <button
                          className="text-blue-600 text-sm underline"
                          onClick={() => {
                            setReplyTarget(replyTarget === msg.id ? null : msg.id);
                            setReplyContent("");
                          }}
                        >
                          답장하기
                        </button>

                        <button
                          className="text-red-600 text-sm underline"
                          onClick={() => handleDeleteMessage(msg.id)}
                        >
                          삭제
                        </button>
                      </div>

                      {replyTarget === msg.id && (() => {
                        const thread = messages
                          .filter(m => (
                            m.rentId === msg.rentId &&
                            (
                              (m.senderUid === user.uid && m.recipientUid === msg.senderUid) ||
                              (m.senderUid === msg.senderUid && m.recipientUid === user.uid)
                            )
                          ))
                          .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));

                        return (
                          <div className="mt-2 space-y-2">
                            <div className="mb-2 text-sm font-semibold text-gray-700">대화 히스토리</div>
                            <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                              {thread.map((t) => (
                                <div
                                  key={t.id}
                                  className={`flex ${t.senderUid === user.uid ? 'justify-end' : 'justify-start'}`}
                                >
                                  <div
                                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm shadow-sm whitespace-pre-wrap ${
                                      t.senderUid === user.uid
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100 text-gray-900'
                                    }`}
                                  >
                                    <div>{t.content}</div>
                                    <div className={`mt-1 text-[11px] ${t.senderUid === user.uid ? 'text-blue-100' : 'text-gray-500'}`}>
                                      {formatTimestamp(t.createdAt)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <textarea
                              className="w-full border p-2 rounded text-sm"
                              placeholder="답장을 입력하세요"
                              value={replyContent}
                              onChange={(e) => setReplyContent(e.target.value)}
                            />
                            <div className="flex justify-end">
                              <button
                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm"
                                onClick={async () => {
                                  await handleReplySend(msg);
                                }}
                              >
                                답장 보내기
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ))}

                  {hasMoreInbox && (
                    <div className="mt-4 flex justify-center">
                      <button
                        className="px-4 py-2 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
                        onClick={() => fetchMessages()}
                        disabled={isMessagesLoading}
                      >
                        {isMessagesLoading ? "불러오는 중..." : "더 불러오기"}
                      </button>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end mt-4">
                <button
                  className="bg-gray-600 text-white px-4 py-2 rounded"
                  onClick={() => setShowInbox(false)}
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}


        {showSafetyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="section-title text-xl font-bold">안전거래 수칙</h2>
                <button
                  type="button"
                  onClick={() => setShowSafetyModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                  aria-label="닫기"
                >
                  ×
                </button>
              </div>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2">
                <li>임대인이 집주인인지 / 서블릿인지 확인</li>
                <li>서블릿일 경우 집주인 동의 여부 확인</li>
                <li>렌트 매물 직접 확인후 계약</li>
                <li>보증금 추가 입금 요청 주의</li>
                <li>시세보다 지나치게 저렴한 매물 주의</li>
                <li>의심스러운 경우 거래를 중단</li>
                <li>krent.ca는 개인 간 거래에 대해 법적 책임을 지지 않습니다.</li>
              </ul>
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setShowSafetyModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}


        {showTermsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded shadow-lg max-w-lg w-full">
              <h2 className="section-title text-lg font-bold mb-4">이용약관</h2>
              <p className="desc-text text-gray-700 mb-4">
                본 사이트는 캐나다 내 한인 커뮤니티를 위한 렌트 정보 제공 서비스입니다.
                이용자는 사실에 기반한 정보를 정확하게 등록해야 하며, 허위정보 또는 사기성 게시물은 금지됩니다.
                이용자는 게시된 정보에 대한 법적 책임을 지며, 운영자는 이에 대한 법적 책임을 지지 않습니다.
                거래는 반드시 본인의 책임 하에 직접 확인하고 진행해야 하며, 사기 피해에 주의하시기 바랍니다.
                부적절하거나 부정확한 정보는 예고 없이 삭제될 수 있습니다.
              </p>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={() => setAgreeTerms(!agreeTerms)}
                />
                <span className="text-sm text-gray-700">이용약관에 동의합니다.</span>


                <button className="text-blue-500 text-sm underline mb-4" onClick={() => { setShowFullTerms(true); setShowTermsModal(false); }}>이용약관 전문 보기</button>
              </div>

              <div className="flex justify-end gap-2">
                <button className="px-4 py-2 bg-gray-400 text-white rounded" onClick={() => setShowTermsModal(false)}>닫기</button>
              </div>
            </div>
          </div>
        )}

        {showFullTerms && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded shadow-lg max-w-2xl w-full overflow-y-auto max-h-[90vh]">
              <h2 className="section-title text-xl font-bold mb-4">이용약관 전문</h2>
              <p className="desc-text text-gray-700 whitespace-pre-wrap mb-6">
                제1조 (목적) 
                본 약관은 ‘krent.ca’(이하 “회사”)가 제공하는 온라인 렌트 매물 정보 서비스(이하 “서비스”)의 이용조건 및 절차, 회사와 회원 간의 권리·의무 및 책임사항 등을 규정함을 목적으로 합니다.

                제2조 (회원의 의무)
                1. 회원은 실명으로 가입하여야 하며, 타인의 명의를 도용하여 가입할 수 없습니다.
                2. 회원은 허위 또는 과장된 정보를 등록하거나, 타인의 권리를 침해하는 행위를 하여서는 안 됩니다.
                3. 회원은 관련 법령과 본 약관에서 규정한 사항을 준수하여야 하며, 위반 시 서비스 이용이 제한될 수 있습니다.

                제3조 (서비스 내용)
                1. 회사는 캐나다 내 거주자를 위한 한인 대상 렌트 매물 등록 및 열람 기능을 제공합니다.
                2. 등록된 정보의 진위, 정확성 및 적법성에 대한 책임은 해당 정보를 등록한 회원에게 있으며, 회사는 이에 대해 어떠한 보증도 하지 않습니다.

                제4조 (정보의 삭제, 신고 및 제재)
                1. 다음 각호에 해당하는 경우, 회사는 사전 통보 없이 게시물을 삭제하거나 회원의 서비스 이용을 제한할 수 있습니다:
                - 허위, 과장 또는 사기성 정보
                - 비속어, 혐오 발언 또는 음란성 콘텐츠
                - 타인의 권리, 명예 또는 재산을 침해하는 게시물
                2. 회원 또는 제3자는 서비스 이용 중 부적절한 게시물이나 이용자를 발견한 경우, 회사 이메일(admin@krent.ca)을 통해 신고할 수 있습니다.
                3. 회사는 신고 내용의 사실 여부를 자체 기준에 따라 판단하며, 필요하다고 판단되는 경우 게시물 삭제, 이용 제한, 계정 정지 등의 조치를 취할 수 있습니다.
                4. 동일하거나 중대한 위반 행위가 반복될 경우, 회사는 회원 자격을 박탈할 수 있습니다.

                제5조 (면책 조항)
                1. 회사는 회원 간에 이루어지는 렌트 계약, 거래, 분쟁 등에 일체 관여하지 않으며, 이에 대해 어떠한 책임도 부담하지 않습니다.
                2. 회사는 회원이 게시한 정보의 정확성, 신뢰성, 적법성 또는 진실성에 대해 책임을 지지 않습니다.
                3. 회사는 회원 간 분쟁 발생 시 중재 의무를 부담하지 않으며, 관련 분쟁은 당사자 간에 해결되어야 합니다.

                제6조 (기타)
                1. 본 약관은 사전 고지 없이 변경될 수 있으며, 변경된 사항은 공지 또는 이메일 등을 통해 안내됩니다.  
                2. 회원이 변경된 약관에 동의하지 않을 경우 서비스 이용을 중단할 수 있으며, 변경 이후에도 서비스를 계속 이용하는 경우 변경된 약관에 동의한 것으로 간주됩니다.
              </p>

              <div className="flex justify-end">
                <button className="px-4 py-2 bg-blue-500 text-white rounded" onClick={() => setShowFullTerms(false)}>닫기</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );

};


